
import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  memo,
  useRef,
} from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../Constants";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler,
);

// ─── Role helpers ────────────────────────────────────────────────────────────
const ROLE = {
  COMPANY: "COMPANY",
  VICE_PRESIDENT: "VICE_PRESIDENT",
  MANAGER: "MANAGER",
  TEAM_LEAD: "TEAM_LEAD",
  EXECUTIVE: "EXECUTIVE",
  HR: "HR",
  SUPER_SUPER_ADMIN: "SUPER_SUPER_ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  RESELLER: "RESELLER",
};

const isOwnerLevel = (t) =>
  [
    ROLE.COMPANY,
    ROLE.SUPER_SUPER_ADMIN,
    ROLE.SUPER_ADMIN,
    ROLE.RESELLER,
  ].includes(t);
const isMidLevel = (t) =>
  [ROLE.VICE_PRESIDENT, ROLE.MANAGER, ROLE.TEAM_LEAD].includes(t);
const isFieldLevel = (t) => t === ROLE.EXECUTIVE;
const isHR = (t) => t === ROLE.HR;

// ─── Token helper ────────────────────────────────────────────────────────────
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const SELF_FETCHING_WIDGETS = new Set([
  "APPOINTMENTS_TODAY",
  "EXEC_WORK_SUMMARY",
  "LEAD_TREND",
  "LEAD_ACTION_REPORT",
  "EXEC_PERFORMANCE",
  "FOLLOWUPS_DUE",
]);

const CATEGORY_ORDER = [
  "leads",
  "reports",
  "task",
  "calendar",
  "hr",
  "issues",
  "invoice",
];

const CATEGORY_LABELS = {
  leads: "Leads",
  reports: "Reports",
  task: "Tasks",
  calendar: "Calendar",
  hr: "HR",
  issues: "Issues",
  invoice: "Invoices",
};

const useInView = (rootMargin = "200px") => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, rootMargin]);
  return [ref, seen];
};

// ─── Sub Components ──────────────────────────────────────────────────────────
const SGAvatar = ({ name = "", size = 52 }) => {
  const parts = name.trim().split(" ");
  const initials =
    parts.length >= 2
      ? parts[0][0] + parts[parts.length - 1][0]
      : (parts[0]?.[0] ?? "?");
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#e0e8f5",
        color: "#0C4550",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size * 0.35,
        flexShrink: 0,
        border: "2px solid #c6e4ec",
        letterSpacing: "0.04em",
      }}
    >
      {initials.toUpperCase()}
    </div>
  );
};

const StarRating = memo(({ rating = 0 }) => {
  return (
    <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((index) => {
        const fill = Math.min(Math.max(rating - (index - 1), 0), 1) * 100;
        return (
          <div
            key={index}
            style={{ position: "relative", width: "16px", height: "16px" }}
          >
            {/* Background empty star */}
            <svg
              viewBox="0 0 24 24"
              fill="#e5e9f0"
              width="16"
              height="16"
              style={{ position: "absolute" }}
            >
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
            {/* Foreground filled yellow star */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: `${fill}%`,
                overflow: "hidden",
              }}
            >
              <svg viewBox="0 0 24 24" fill="#f59e0b" width="16" height="16">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            </div>
          </div>
        );
      })}
    </div>
  );
});

const StatBadge = memo(({ label, value, icon, accent = "#673AB7", sub, onClick }) => (
  <div 
    className="crm-stat-card" 
    style={{ "--accent": accent, cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <div className="crm-stat-icon">{icon}</div>
    <div className="crm-stat-body">
      <div className="crm-stat-val">{value ?? "—"}</div>
      <div className="crm-stat-label">{label}</div>
      {sub && <div className="crm-stat-sub">{sub}</div>}
    </div>
    <div className="crm-stat-bar" />
  </div>
));

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="crm-section-header">
    <div>
      <h2 className="crm-section-title">{title}</h2>
      {subtitle && <p className="crm-section-sub">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const EmptyState = ({ message = "No data available" }) => (
  <div className="crm-empty">
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
    <p>{message}</p>
  </div>
);

const Skeleton = ({ h = 20, w = "100%", r = 6 }) => (
  <div
    className="crm-skeleton"
    style={{ height: h, width: w, borderRadius: r }}
  />
);

const Avatar = ({ name = "", color = "#673AB7" }) => {
  const parts = name.trim().split(" ");
  const initials =
    parts.length >= 2
      ? parts[0][0] + parts[parts.length - 1][0]
      : (parts[0]?.[0] ?? "?");
  return (
    <div className="crm-avatar" style={{ background: color + "22", color }}>
      {initials.toUpperCase()}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    PRESENT: { bg: "#dcfce7", color: "#16a34a", label: "Present" },
    ABSENT: { bg: "#fee2e2", color: "#dc2626", label: "Absent" },
    LATE: { bg: "#fef9c3", color: "#ca8a04", label: "Late" },
    ON_LEAVE: { bg: "#ede9fe", color: "#7c3aed", label: "On Leave" },
    New: { bg: "#eff6ff", color: "#2563eb", label: "New" },
    "In Progress": { bg: "#fef3c7", color: "#d97706", label: "In Progress" },
    Contacted: { bg: "#f0fdf4", color: "#16a34a", label: "Contacted" },
    SCHEDULED: { bg: "#e0e8f5", color: "#2563eb", label: "SCHEDULED" },
    Qualified: { bg: "#ede9fe", color: "#7c3aed", label: "Qualified" },
    Unqualified: { bg: "#fee2e2", color: "#dc2626", label: "Unqualified" },
  };
  const s = map[status] ?? { bg: "#f3f4f6", color: "#6b7280", label: status };
  return (
    <span className="crm-pill" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

const Tabs = memo(({ tabs, active, onChange }) => (
  <div className="crm-tabs">
    {tabs.map((t) => (
      <button
        key={t.name}
        className={`crm-tab ${active === t.name ? "crm-tab--active" : ""}`}
        onClick={() => onChange(t.name)}
      >
        {t.name}
        <span className="crm-tab-badge">{t.count ?? 0}</span>
      </button>
    ))}
  </div>
));

const LeadRow = memo(({ lead, showFollowup, onClick }) => (
  <div
    className="crm-list-row"
    onClick={onClick ? () => onClick(lead) : undefined}
    style={onClick ? { cursor: "pointer" } : undefined}
  >
    <Avatar name={lead.name || lead.leadName || "?"} />
    <div className="crm-list-body">
      <div className="crm-list-name">{lead.name || lead.leadName}</div>
      <div className="crm-list-meta">{lead.phone || lead.contactNumber}</div>
      {showFollowup && lead.followUps?.[0] && (
        <div className="crm-list-followup">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {(() => {
            const { nextFollowUpDate, nextFollowUpTime } = lead.followUps[0];
            if (!nextFollowUpDate) return "N/A";
            const d = new Date(
              nextFollowUpTime
                ? `${nextFollowUpDate}T${nextFollowUpTime}`
                : nextFollowUpDate,
            );
            return (
              d.toLocaleDateString("en-IN") +
              (nextFollowUpTime
                ? " " +
                  d.toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "")
            );
          })()}
        </div>
      )}
    </div>
    {lead.statusName && <StatusPill status={lead.statusName} />}
  </div>
));

const Card = ({ children, className = "", style = {} }) => (
  <div className={`crm-card ${className}`} style={style}>
    {children}
  </div>
);

// ──────────────────────────────────────────────────────────────────────────────
//  WIDGET COMPONENTS
// ──────────────────────────────────────────────────────────────────────────────

const UnifiedMainCard = memo(
  ({
    activeTab,
    setActiveTab,
    role,
    tabs,
    loadingDash,
    recentLeads,
    loadingRecent,
    onLeadClick,
    onViewAllLeads,
    execSummary,
    loadingExec,
    appointments,
    loadingAppt,
    apptFilter,
    setApptFilter,
    execRatings,
    loadingExecRatings,
  }) => {
    const [todaysActive, setTodaysActive] = useState(tabs[0]?.name ?? "");
    useEffect(() => {
      if (tabs[0]?.name && !todaysActive) setTodaysActive(tabs[0].name);
    }, [tabs[0]?.name]);
    const activeLeads =
      tabs.find((t) => t.name === (todaysActive || tabs[0]?.name))?.leads ?? [];

    return (
      <Card>
        <div className="crm-unified-header">
          <button
            className={`crm-unified-tab ${activeTab === "todays" ? "active" : ""}`}
            onClick={() => setActiveTab("todays")}
          >
            Today's Leads
          </button>
          <button
            className={`crm-unified-tab ${activeTab === "recent" ? "active" : ""}`}
            onClick={() => setActiveTab("recent")}
          >
            Recently Updated Leads
          </button>
          {role === ROLE.COMPANY && (
            <button
              className={`crm-unified-tab ${activeTab === "exec" ? "active" : ""}`}
              onClick={() => setActiveTab("exec")}
            >
              Executive Work Summary
            </button>
          )}
          <button
            className={`crm-unified-tab ${activeTab === "appointments" ? "active" : ""}`}
            onClick={() => setActiveTab("appointments")}
          >
            Appointments
          </button>
          {!isFieldLevel(role) && (
            <button
              className={`crm-unified-tab ${activeTab === "ratings" ? "active" : ""}`}
              onClick={() => setActiveTab("ratings")}
            >
              Executive Ratings
            </button>
          )}
        </div>

        {activeTab === "todays" && (
          <>
            <SectionHeader
              title="Today's Leads"
              subtitle="Live pipeline snapshot"
            />
            {loadingDash ? (
              <div className="crm-card-body">
                <Skeleton h={18} />
                <Skeleton h={18} />
                <Skeleton h={18} />
              </div>
            ) : (
              <>
                <Tabs
                  tabs={tabs}
                  active={todaysActive || tabs[0]?.name}
                  onChange={setTodaysActive}
                />
                <div
                  className="crm-card-body crm-scroll"
                  style={{ maxHeight: "340px" }}
                >
                  {activeLeads.length > 0 && onViewAllLeads && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        paddingBottom: "10px",
                      }}
                    >
                      <button
                        className="crm-btn-ghost"
                        onClick={() =>
                          onViewAllLeads(
                            todaysActive || tabs[0]?.name,
                            activeLeads,
                          )
                        }
                      >
                        View Full →
                      </button>
                    </div>
                  )}
                  {activeLeads.length === 0 ? (
                    <EmptyState message="No leads in this status today" />
                  ) : (
                    activeLeads.map((l) => (
                      <LeadRow
                        key={l.leadId || l.id}
                        lead={l}
                        showFollowup={todaysActive === "Followups"}
                        onClick={onLeadClick}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === "recent" && (
          <>
            <SectionHeader
              title="Recently Updated Leads"
              subtitle="Latest pipeline activity"
            />
            {loadingRecent ? (
              <div className="crm-card-body">
                <Skeleton h={18} />
                <Skeleton h={18} />
                <Skeleton h={18} />
              </div>
            ) : (
              <div
                className="crm-card-body crm-scroll"
                style={{ maxHeight: "340px" }}
              >
                {recentLeads.length === 0 ? (
                  <EmptyState message="No recently updated leads found" />
                ) : (
                  recentLeads.map((l) => (
                    <div
                      key={l.leadId || l.id}
                      className="crm-list-row"
                      style={{ cursor: "pointer" }}
                      onClick={() => onLeadClick(l)}
                    >
                      <Avatar name={l.name || l.leadName || "?"} />
                      <div className="crm-list-body">
                        <div className="crm-list-name">
                          {l.name || l.leadName}
                        </div>
                        <div className="crm-list-meta">
                          {l.phone || l.contactNumber}
                        </div>
                        {l.assignedToName && (
                          <div
                            className="crm-list-meta"
                            style={{ color: "#0C4550", fontWeight: 600 }}
                          >
                            Assigned to: {l.assignedToName}
                          </div>
                        )}
                      </div>
                      {l.statusName && <StatusPill status={l.statusName} />}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "exec" && role === ROLE.COMPANY && (
          <>
            <SectionHeader
              title="Executive Work Summary"
              subtitle="Leads worked this period"
            />
            {loadingExec ? (
              <div className="crm-card-body">
                <Skeleton h={18} />
                <Skeleton h={18} />
                <Skeleton h={18} />
              </div>
            ) : (
              <div
                className="crm-card-body crm-scroll"
                style={{ maxHeight: "340px" }}
              >
                {execSummary.length === 0 ? (
                  <EmptyState message="No executive data available" />
                ) : (
                  execSummary.map((exec, i) => {
                    const colors = [
                      "#673AB7",
                      "#10b981",
                      "#f59e0b",
                      "#ef4444",
                      "#8b5cf6",
                      "#06b6d4",
                    ];
                    return (
                      <div key={exec.executiveId} className="crm-team-row">
                        <Avatar
                          name={exec.executiveName || "?"}
                          color={colors[i % colors.length]}
                        />
                        <div className="crm-list-body">
                          <div className="crm-list-name">
                            {exec.executiveName}
                          </div>
                          <div className="crm-list-meta">
                            Lead IDs assigned: {exec.leadIds?.length || 0}
                          </div>
                        </div>
                        <div className="crm-team-nums">
                          <span className="crm-team-num-big">
                            {exec.totalLeadsWorked}
                          </span>
                          <span className="crm-team-num-small">
                            leads worked
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "appointments" && (
          <>
            <SectionHeader
              title="Appointments"
              subtitle="Your scheduled appointments"
            />
            {!isFieldLevel(role) && (
              <div
                style={{
                  padding: "0 18px 10px",
                  display: "flex",
                  gap: "8px",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <button
                  className="crm-pill"
                  style={
                    apptFilter === "all"
                      ? {
                          background: "#0C4550",
                          color: "#fff",
                          border: "1px solid #0C4550",
                          cursor: "pointer",
                        }
                      : {
                          background: "#fff",
                          border: "1px solid #e5e9f0",
                          color: "#4b5563",
                          cursor: "pointer",
                        }
                  }
                  onClick={() => setApptFilter("all")}
                >
                  All Appointments
                </button>
                <button
                  className="crm-pill"
                  style={
                    apptFilter === "today"
                      ? {
                          background: "#0C4550",
                          color: "#fff",
                          border: "1px solid #0C4550",
                          cursor: "pointer",
                        }
                      : {
                          background: "#fff",
                          border: "1px solid #e5e9f0",
                          color: "#4b5563",
                          cursor: "pointer",
                        }
                  }
                  onClick={() => setApptFilter("today")}
                >
                  Today's Appointments
                </button>
              </div>
            )}
            <div
              className="crm-card-body crm-scroll"
              style={{
                maxHeight: "440px",
                padding: "18px",
                background: "#fff",
              }}
            >
              {loadingAppt ? (
                <>
                  <Skeleton h={18} />
                  <Skeleton h={18} />
                </>
              ) : appointments.length === 0 ? (
                <EmptyState message="No appointments found" />
              ) : (
                appointments.map((apt, i) => (
                  <div key={apt.appointmentId || i} className="crm-appt-card">
                    <div className="crm-appt-header">
                      <div className="crm-appt-dept">
                        <span className="crm-appt-label">Department:</span>{" "}
                        {apt.departmentName || "N/A"}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span className="crm-appt-label">Status:</span>
                        <StatusPill status={apt.status || "SCHEDULED"} />
                      </div>
                    </div>

                    <div className="crm-appt-row">
                      <span className="crm-appt-label">Person:</span>{" "}
                      {apt.personName || "N/A"}
                      {apt.personPosition && (
                        <span className="crm-appt-sub-text">
                          {" "}
                          • {apt.personPosition}
                        </span>
                      )}
                    </div>

                    <div className="crm-appt-datetime">
                      <div className="crm-appt-dt-item">
                        <span style={{ marginRight: "6px", color: "#0ea5e9" }}>
                          📅
                        </span>
                        <span className="crm-appt-label">Date:</span>{" "}
                        {apt.preferredDate || "-"}
                      </div>
                      <div className="crm-appt-dt-item">
                        <span style={{ marginRight: "6px", color: "#6b7280" }}>
                          🕒
                        </span>
                        <span className="crm-appt-label">Time:</span>{" "}
                        {apt.preferredTime || "-"}
                      </div>
                    </div>

                    {apt.leadName && (
                      <div className="crm-appt-row">
                        <span className="crm-appt-label">Lead Info:</span>{" "}
                        {apt.leadName}{" "}
                        {apt.leadPhone ? `(${apt.leadPhone})` : ""}
                      </div>
                    )}

                    {apt.notes && (
                      <div className="crm-appt-row">
                        <span className="crm-appt-label">Notes:</span>{" "}
                        {apt.notes}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {activeTab === "ratings" && !isFieldLevel(role) && (
          <>
            <SectionHeader
              title="Executive Ratings"
              subtitle="Transcription based executive performance"
            />
            {loadingExecRatings ? (
              <div className="crm-card-body">
                <Skeleton h={18} />
                <Skeleton h={18} />
                <Skeleton h={18} />
              </div>
            ) : (
              <div
                className="crm-card-body crm-scroll"
                style={{ maxHeight: "340px" }}
              >
                {execRatings.length === 0 ? (
                  <EmptyState message="No executive ratings available" />
                ) : (
                  execRatings.map((rating, i) => (
                    <div key={rating.executiveId || i} className="crm-list-row">
                      <Avatar name={rating.executiveName || "?"} />
                      <div className="crm-list-body">
                        <div className="crm-list-name">
                          {rating.executiveName || "Unknown Executive"}
                        </div>
                        <div className="crm-list-meta">
                          Total Rated Calls: {rating.totalRatedCalls ?? 0}
                        </div>
                      </div>
                      {rating.averageRating !== undefined && (
                        <div
                          className="crm-team-nums"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <StarRating rating={rating.averageRating} />
                          <span className="crm-team-num-small">avg rating</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </Card>
    );
  },
);

const TasksCard = memo(({ tasks, loading }) => (
  <Card>
    <SectionHeader
      title="Today's Tasks"
      subtitle="Due today across your scope"
    />
    {loading ? (
      <div className="crm-card-body">
        <Skeleton h={18} />
        <Skeleton h={18} />
      </div>
    ) : (
      <div className="crm-card-body crm-scroll">
        {tasks.length === 0 ? (
          <EmptyState message="No tasks due today" />
        ) : (
          tasks.map((task) => (
            <div key={task.taskId} className="crm-list-row">
              <div className="crm-task-dot" />
              <div className="crm-list-body">
                <div className="crm-list-name">{task.subject}</div>
                <div className="crm-list-meta">
                  {task.assignedTo?.map((u) => u.name).join(", ")}
                </div>
              </div>
              {task.priority && (
                <span
                  className="crm-pill"
                  style={{
                    background:
                      task.priority === "High"
                        ? "#fee2e2"
                        : task.priority === "Medium"
                          ? "#fef9c3"
                          : "#f0fdf4",
                    color:
                      task.priority === "High"
                        ? "#dc2626"
                        : task.priority === "Medium"
                          ? "#ca8a04"
                          : "#16a34a",
                  }}
                >
                  {task.priority}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    )}
  </Card>
));

const ScheduleCard = memo(({ reminders, events, hasReminder, hasCalendar }) => {
  const tabs = [
    hasReminder && { name: "Reminders", count: reminders.length },
    hasCalendar && { name: "Events", count: events.length },
  ].filter(Boolean);
  const [active, setActive] = useState(tabs[0]?.name ?? "Reminders");
  const items = active === "Reminders" ? reminders : events;

  return (
    <Card>
      <SectionHeader
        title="Today's Schedule"
        subtitle="Reminders & calendar events"
      />
      {tabs.length > 0 && (
        <Tabs tabs={tabs} active={active} onChange={setActive} />
      )}
      <div className="crm-card-body crm-scroll">
        {items.length === 0 ? (
          <EmptyState message={`No ${active.toLowerCase()} today`} />
        ) : (
          items.map((item) => (
            <div key={item.reminderId || item.eventId} className="crm-list-row">
              <div
                className="crm-task-dot"
                style={{
                  background: active === "Reminders" ? "#673AB7" : "#10b981",
                }}
              />
              <div className="crm-list-body">
                <div className="crm-list-name">
                  {active === "Reminders" ? item.message : item.title}
                </div>
                <div className="crm-list-meta">
                  {item.time || item.description}
                </div>
              </div>
              {active === "Reminders" && item.reminderType && (
                <span
                  className="crm-pill"
                  style={{ background: "#f3e8ff", color: "#673AB7" }}
                >
                  {item.reminderType}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
});

const AttendanceCard = memo(({ attendance, loading }) => {
  const stats = useMemo(() => {
    const present = attendance.filter((a) => a.status === "PRESENT").length;
    const absent = attendance.filter((a) => a.status === "ABSENT").length;
    const late = attendance.filter((a) => a.status === "LATE").length;
    const onLeave = attendance.filter((a) => a.status === "ON_LEAVE").length;
    return { present, absent, late, onLeave };
  }, [attendance]);

  return (
    <Card>
      <SectionHeader
        title="Today's Attendance"
        subtitle="Team check-in status"
      />
      <div className="crm-mini-stats">
        <div className="crm-mini-stat" style={{ "--c": "#16a34a" }}>
          <span className="crm-mini-val">{stats.present}</span>
          <span className="crm-mini-label">Present</span>
        </div>
        <div className="crm-mini-stat" style={{ "--c": "#dc2626" }}>
          <span className="crm-mini-val">{stats.absent}</span>
          <span className="crm-mini-label">Absent</span>
        </div>
        <div className="crm-mini-stat" style={{ "--c": "#ca8a04" }}>
          <span className="crm-mini-val">{stats.late}</span>
          <span className="crm-mini-label">Late</span>
        </div>
        <div className="crm-mini-stat" style={{ "--c": "#7c3aed" }}>
          <span className="crm-mini-val">{stats.onLeave}</span>
          <span className="crm-mini-label">On Leave</span>
        </div>
      </div>
      {loading ? (
        <div className="crm-card-body">
          <Skeleton h={16} />
          <Skeleton h={16} />
        </div>
      ) : (
        <div className="crm-card-body crm-scroll">
          {attendance.length === 0 ? (
            <EmptyState message="No attendance data today" />
          ) : (
            attendance.slice(0, 8).map((a) => (
              <div key={a.userId} className="crm-list-row">
                <Avatar
                  name={a.username || "?"}
                  color={
                    a.status === "PRESENT"
                      ? "#16a34a"
                      : a.status === "ABSENT"
                        ? "#dc2626"
                        : a.status === "LATE"
                          ? "#ca8a04"
                          : "#7c3aed"
                  }
                />
                <div className="crm-list-body">
                  <div className="crm-list-name">{a.username}</div>
                  <div className="crm-list-meta">
                    {a.checkInTime
                      ? `In: ${new Date(a.checkInTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
                      : "Not checked in"}
                  </div>
                </div>
                <StatusPill status={a.status} />
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
});

const LeaveRequestsCard = memo(({ leaves, loading }) => (
  <Card>
    <SectionHeader
      title="Pending Leave Requests"
      subtitle="Awaiting your approval"
      action={
        leaves.length > 0 && (
          <span
            className="crm-pill"
            style={{ background: "#fef3c7", color: "#d97706" }}
          >
            {leaves.length} pending
          </span>
        )
      }
    />
    {loading ? (
      <div className="crm-card-body">
        <Skeleton h={18} />
        <Skeleton h={18} />
      </div>
    ) : (
      <div className="crm-card-body crm-scroll">
        {leaves.length === 0 ? (
          <EmptyState message="No pending leave requests" />
        ) : (
          leaves.map((l) => (
            <div key={l.id} className="crm-list-row">
              <Avatar name={l.username || "?"} color="#7c3aed" />
              <div className="crm-list-body">
                <div className="crm-list-name">{l.username}</div>
                <div className="crm-list-meta">
                  {l.leaveType} ·{" "}
                  {new Date(l.fromDate).toLocaleDateString("en-IN")}
                  {l.fromDate !== l.toDate &&
                    ` – ${new Date(l.toDate).toLocaleDateString("en-IN")}`}
                </div>
                {l.reason && (
                  <div
                    className="crm-list-meta"
                    style={{ fontStyle: "italic" }}
                  >
                    {l.reason}
                  </div>
                )}
              </div>
              <StatusPill status="PENDING" />
            </div>
          ))
        )}
      </div>
    )}
  </Card>
));

const TeamPerformanceCard = memo(({ team, loading }) => (
  <Card>
    <SectionHeader
      title="Team Performance"
      subtitle="Leads & conversions by member"
    />
    {loading ? (
      <div className="crm-card-body">
        <Skeleton h={18} />
        <Skeleton h={18} />
      </div>
    ) : (
      <div className="crm-card-body crm-scroll">
        {team.length === 0 ? (
          <EmptyState message="No team data available" />
        ) : (
          team.slice(0, 8).map((u, i) => {
            const convRate =
              u.totalLeads > 0
                ? Math.round((u.conversions / u.totalLeads) * 100)
                : 0;
            const colors = [
              "#673AB7",
              "#10b981",
              "#f59e0b",
              "#ef4444",
              "#8b5cf6",
              "#06b6d4",
            ];
            return (
              <div key={u.userId} className="crm-team-row">
                <Avatar
                  name={u.userName || "?"}
                  color={colors[i % colors.length]}
                />
                <div className="crm-list-body">
                  <div className="crm-list-name">{u.userName}</div>
                  <div className="crm-team-bar-wrap">
                    <div className="crm-team-bar-track">
                      <div
                        className="crm-team-bar-fill"
                        style={{
                          width: `${Math.min(convRate, 100)}%`,
                          background: colors[i % colors.length],
                        }}
                      />
                    </div>
                    <span className="crm-team-bar-label">{convRate}%</span>
                  </div>
                </div>
                <div className="crm-team-nums">
                  <span className="crm-team-num-big">{u.totalLeads}</span>
                  <span className="crm-team-num-small">
                    {u.conversions} conv.
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    )}
  </Card>
));

const SourceDonutCard = memo(({ sources, loading, onFilter }) => {
  const colors = [
    "#673AB7",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#ec4899",
    "#84cc16",
  ];
  const chartData = useMemo(
    () => ({
      labels: sources.map((s) => s.sourceName),
      datasets: [
        {
          data: sources.map((s) => s.count),
          backgroundColor: colors.slice(0, sources.length),
          borderWidth: 2,
          borderColor: "#fff",
          hoverBorderColor: "#fff",
        },
      ],
    }),
    [sources],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: "#6b7280",
            font: { size: 11 },
            padding: 10,
            boxWidth: 10,
          },
        },
        tooltip: { callbacks: { label: (c) => ` ${c.label}: ${c.raw}` } },
      },
      cutout: "68%",
    }),
    [],
  );

  const total = sources.reduce((a, b) => a + b.count, 0);

  return (
    <Card>
      <SectionHeader
        title="Leads by Source"
        subtitle="Where your leads come from"
        action={
          <button className="crm-btn-ghost" onClick={onFilter}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filter
          </button>
        }
      />
      <div
        className="crm-card-body"
        style={{ height: 260, position: "relative" }}
      >
        {loading ? (
          <div className="crm-center">
            <div className="crm-spinner" />
          </div>
        ) : sources.length === 0 ? (
          <EmptyState message="Use Filter to break leads down by source, project or date range" />
        ) : (
          <>
            <Doughnut data={chartData} options={chartOptions} />
            <div className="crm-donut-center">
              <div className="crm-donut-total">{total}</div>
              <div className="crm-donut-label">Total</div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
});

const ActivityChartCard = memo(
  ({
    users,
    selectedUser,
    onUserChange,
    selectedMonth,
    onMonthChange,
    chartData,
    loading,
  }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
    const pickerRef = useRef(null);
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const fullMonthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    useEffect(() => {
      if (selectedMonth) setPickerYear(parseInt(selectedMonth.split("-")[1]));
    }, [selectedMonth]);

    useEffect(() => {
      const handler = (e) => {
        if (pickerRef.current && !pickerRef.current.contains(e.target))
          setPickerOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    const displayMonth = useMemo(() => {
      if (!selectedMonth) return "Select month";
      const [m, y] = selectedMonth.split("-");
      return `${fullMonthNames[parseInt(m) - 1]} ${y}`;
    }, [selectedMonth]);

    const barOptions = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxRotation: 45, minRotation: 45, font: { size: 9 } },
          },
          y: {
            beginAtZero: true,
            max: 12,
            ticks: {
              callback: (v) => v + "h",
              stepSize: 2,
              font: { size: 10 },
            },
            grid: { color: "#f1f5f9" },
          },
        },
        plugins: {
          legend: {
            position: "top",
            labels: { usePointStyle: true, padding: 12, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (c) => `${c.dataset.label}: ${c.raw.toFixed(1)}h`,
            },
          },
        },
        interaction: { intersect: false, mode: "index" },
      }),
      [],
    );

    return (
      <Card style={{ height: "100%" }}>
        <div className="crm-activity-header">
          <div>
            <div className="crm-section-title">Monthly Activity</div>
            <div className="crm-section-sub">{displayMonth}</div>
          </div>
          <div className="crm-activity-controls">
            <select
              className="crm-select"
              value={selectedUser}
              onChange={(e) => onUserChange(e.target.value)}
            >
              <option value="">Select user</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fname} {u.lname}
                </option>
              ))}
            </select>
            <div className="crm-month-picker" ref={pickerRef}>
              <button
                className="crm-select crm-month-trigger"
                onClick={() => setPickerOpen(!pickerOpen)}
              >
                {displayMonth}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ marginLeft: 6, flexShrink: 0 }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {pickerOpen && (
                <div className="crm-month-dropdown">
                  <div className="crm-month-nav">
                    <button onClick={() => setPickerYear((y) => y - 1)}>
                      ‹
                    </button>
                    <span>{pickerYear}</span>
                    <button onClick={() => setPickerYear((y) => y + 1)}>
                      ›
                    </button>
                  </div>
                  <div className="crm-month-grid">
                    {monthNames.map((m, i) => {
                      const val = `${String(i + 1).padStart(2, "0")}-${pickerYear}`;
                      const isSel = selectedMonth === val;
                      const isCurr =
                        new Date().getMonth() === i &&
                        new Date().getFullYear() === pickerYear;
                      return (
                        <button
                          key={m}
                          className={`crm-month-btn ${isSel ? "crm-month-btn--sel" : ""} ${isCurr && !isSel ? "crm-month-btn--curr" : ""}`}
                          onClick={() => {
                            onMonthChange(val);
                            setPickerOpen(false);
                          }}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="crm-chart-wrap">
          {loading ? (
            <div className="crm-center">
              <div className="crm-spinner" />
            </div>
          ) : chartData ? (
            <Bar data={chartData} options={barOptions} />
          ) : (
            <EmptyState message="Select a user and month to view activity" />
          )}
        </div>
      </Card>
    );
  },
);

const FilterModal = memo(
  ({
    open,
    onClose,
    filters,
    onChange,
    onApply,
    onReset,
    users,
    labels,
    statusOptions,
    sourceOptions,
  }) => {
    if (!open) return null;
    return (
      <div className="crm-modal-overlay" onClick={onClose}>
        <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="crm-modal-header">
            <h3>Filter Leads Analysis</h3>
            <button className="crm-modal-close" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="crm-modal-body">
            {[
              {
                label: "Created by",
                name: "createdById",
                opts: users,
                getOpt: (u) => ({
                  value: u.id,
                  label: `${u.fname} ${u.lname}`,
                }),
              },
              {
                label: "Assigned to",
                name: "assignedToId",
                opts: users,
                getOpt: (u) => ({
                  value: u.id,
                  label: `${u.fname} ${u.lname}`,
                }),
              },
              {
                label: "Project",
                name: "projectName",
                opts: labels,
                getOpt: (l) => ({ value: l.name || l, label: l.name || l }),
              },
            ].map(({ label, name, opts, getOpt }) => (
              <div className="crm-form-row" key={name}>
                <label className="crm-form-label">{label}</label>
                <select
                  className="crm-form-select"
                  name={name}
                  value={filters[name]}
                  onChange={onChange}
                >
                  <option value="">All</option>
                  {opts.map((o, i) => {
                    const { value, label } = getOpt(o);
                    return (
                      <option key={i} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            ))}
            <div className="crm-form-grid">
              <div className="crm-form-row">
                <label className="crm-form-label">Status</label>
                <select
                  className="crm-form-select"
                  name="statusName"
                  value={filters.statusName}
                  onChange={onChange}
                >
                  <option value="">All</option>
                  {statusOptions.map((s, i) => (
                    <option key={i} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="crm-form-row">
                <label className="crm-form-label">Source</label>
                <select
                  className="crm-form-select"
                  name="sourceName"
                  value={filters.sourceName}
                  onChange={onChange}
                >
                  <option value="">All</option>
                  {sourceOptions.map((s, i) => (
                    <option key={i} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="crm-form-grid">
              <div className="crm-form-row">
                <label className="crm-form-label">From date</label>
                <input
                  className="crm-form-select"
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={onChange}
                />
              </div>
              <div className="crm-form-row">
                <label className="crm-form-label">To date</label>
                <input
                  className="crm-form-select"
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={onChange}
                />
              </div>
            </div>
          </div>
          <div className="crm-modal-footer">
            <button className="crm-btn-ghost" onClick={onReset}>
              Reset
            </button>
            <button className="crm-btn-primary" onClick={onApply}>
              Apply filters
            </button>
          </div>
        </div>
      </div>
    );
  },
);

const LeadHistoryModal = memo(
  ({ open, onClose, historyData, loading, lead }) => {
    if (!open) return null;

    const formatDateTime = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const strHours = String(hours).padStart(2, "0");
      return `${day}-${month}-${year} ${strHours}:${minutes} ${ampm}`;
    };

    const renderChange = (item, idx) => {
      if (item.changeType === "lead created") {
        return (
          <div key={idx} className="sg2-history-row">
            <span className="sg2-history-dot sg2-history-dot--green" />
            <span className="sg2-history-created-text">Lead was created</span>
          </div>
        );
      }

      if (item.changeType && item.changeType.includes("UPDATED_FIELDS")) {
        const changeText = item.changeType.replace("UPDATED_FIELDS: ", "");
        const changes = [];
        const fieldRegex = /([^:]+?)\s*\[From:\s*([^,]+),\s*To:\s*([^\]]+)\]/g;
        let match;

        while ((match = fieldRegex.exec(changeText)) !== null) {
          const field = match[1].trim();
          const from = match[2].trim() || "Empty";
          const to = match[3].trim();
          if (field.toLowerCase().includes("color")) continue;
          changes.push({ field, from, to });
        }

        if (changes.length === 0) {
          const filteredLines = changeText
            .split(",")
            .filter((line) => !line.toLowerCase().includes("color"));
          return filteredLines.map((line, i) => (
            <div key={i} className="sg2-history-row">
              <span className="sg2-history-dot" />
              <span className="sg2-history-created-text">{line.trim()}</span>
            </div>
          ));
        }

        return changes.map((c, i) => (
          <div key={i} className="sg2-history-change">
            <div className="sg2-history-change-field">{c.field}</div>
            <div className="sg2-history-change-values">
              <span className="sg2-history-from">{c.from}</span>
              <span className="sg2-history-arrow">→</span>
              <span className="sg2-history-to">{c.to}</span>
            </div>
          </div>
        ));
      }

      return (
        <div key={idx} className="sg2-history-row">
          <span className="sg2-history-dot" />
          <span className="sg2-history-created-text">
            {item.changeType ||
              item.statusName ||
              item.effectiveStatus ||
              item.action ||
              "Updated"}
          </span>
          {item.reason && (
            <div
              style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
            >
              Reason: {item.reason}
            </div>
          )}
          {item.notes && (
            <div
              style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
            >
              Notes: {item.notes}
            </div>
          )}
        </div>
      );
    };

    const sorted = [...(historyData || [])].sort(
      (a, b) =>
        new Date(b.createdAt || b.updatedAt) -
        new Date(a.createdAt || a.updatedAt),
    );

    return (
      <div className="crm-modal-overlay" onClick={onClose}>
        <div
          className="crm-modal sg2-history-modal-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-modal-header">
            <h3>Lead History</h3>
            <button className="crm-modal-close" onClick={onClose}>
              ✕
            </button>
          </div>

          <div className="sg2-history-lead-strip">
            <SGAvatar name={lead?.name || lead?.leadName || "?"} size={36} />
            <div className="sg2-history-lead-meta">
              <span className="sg2-history-lead-name">
                {lead?.name || lead?.leadName}
              </span>
              <div className="sg2-history-lead-sub">
                {lead?.phone && <span>📞 {lead.phone}</span>}
                {lead?.email && <span>✉️ {lead.email}</span>}
                {lead?.companyName && <span>🏢 {lead.companyName}</span>}
              </div>
            </div>
          </div>

          <div className="crm-modal-body sg2-history-body-wrap">
            {loading ? (
              <div className="crm-center" style={{ padding: "30px 0" }}>
                <div className="crm-spinner" />
              </div>
            ) : sorted.length === 0 ? (
              <p className="sg2-history-empty">
                No history available for this lead.
              </p>
            ) : (
              sorted.map((item, idx) => (
                <div key={item.historyId || idx} className="sg2-history-card">
                  <div className="sg2-history-card-top">
                    <div className="sg2-history-avatar">
                      {(item.changedByName || item.updatedByName || "U")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div>
                      <div className="sg2-history-changer">
                        {item.changedByName ||
                          item.updatedByName ||
                          "Unknown User"}
                      </div>
                      <div className="sg2-history-time">
                        {formatDateTime(item.createdAt || item.updatedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="sg2-history-card-body">
                    {renderChange(item, idx)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  },
);

const WIDGET_ICONS = {
  leads: "📈",
  reports: "📊",
  hr: "🧑‍🤝‍🧑",
  issues: "🎫",
  task: "✅",
  calendar: "🗓️",
  invoice: "💰",
};

const WIDGET_COLORS = {
  leads: "#673AB7",
  reports: "#0ea5e9",
  hr: "#7c3aed",
  issues: "#ef4444",
  task: "#10b981",
  calendar: "#f59e0b",
  invoice: "#16a34a",
};

const NAME_KEYS = [
  "statusName",
  "sourceName",
  "projectName",
  "executiveName",
  "executiveId",
  "userName",
  "username",
  "name",
  "label",
  "title",
  "subject",
  "message",
];
const VALUE_KEYS = [
  "count",
  "totalActions",
  "totalBilled",
  "totalLeads",
  "invoiceCount",
  "averageRating",
  "totalRatedCalls",
  "conversions",
];

const DynamicTimeBreakdownTable = ({ items, nameKey, accent }) => {
  const maxTotal = Math.max(...items.map((it) => it.total || 0), 1);
  return (
    <div className="dw-time-table">
      <div className="dw-time-row dw-time-head">
        <span className="dw-time-name">
          {nameKey === "projectName" ? "Project" : "Source"}
        </span>
        <span>Today</span>
        <span>Yesterday</span>
        <span>7 days</span>
        <span>Total</span>
      </div>
      {items.slice(0, 10).map((it, i) => (
        <div key={(it[nameKey] || "—") + i} className="dw-time-row">
          <span className="dw-time-name" title={it[nameKey]}>
            <span
              className="dw-time-dot"
              style={{ background: it.projectNameColor || accent }}
            />
            {it[nameKey] || "—"}
          </span>
          <span>{it.today ?? 0}</span>
          <span>{it.yesterday ?? 0}</span>
          <span>{it.last7Days ?? 0}</span>
          <span className="dw-time-total">
            {it.total ?? 0}
            <span className="dw-time-total-track">
              <span
                className="dw-time-total-fill"
                style={{
                  width: `${Math.round(((it.total || 0) / maxTotal) * 100)}%`,
                  background: accent,
                }}
              />
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

const humanizeKey = (key) =>
  key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

const pickField = (obj, candidates, fallback) => {
  for (const k of candidates) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return fallback;
};

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const DynamicStatGrid = ({ data }) => {
  const entries = Object.entries(data).filter(
    ([, v]) => typeof v === "number" || typeof v === "string",
  );
  if (entries.length === 0)
    return <EmptyState message="No data for this widget" />;
  return (
    <div className="dw-stat-grid">
      {entries.slice(0, 8).map(([k, v]) => (
        <div key={k} className="dw-stat-cell">
          <span className="dw-stat-cell-val">
            {typeof v === "number" ? Math.round(v * 100) / 100 : v}
          </span>
          <span className="dw-stat-cell-label">{humanizeKey(k)}</span>
        </div>
      ))}
    </div>
  );
};

const DynamicBarList = ({ items, accent }) => {
  const max = Math.max(
    ...items.map((it) => Number(pickField(it, VALUE_KEYS, 0)) || 0),
    1,
  );
  return (
    <div className="dw-bar-list">
      {items.slice(0, 8).map((it, i) => {
        const name = String(pickField(it, NAME_KEYS, "—"));
        const value = Number(pickField(it, VALUE_KEYS, 0)) || 0;
        const pct = Math.min(100, Math.round((value / max) * 100));
        const color = it.statusColor || it.projectNameColor || accent;
        return (
          <div key={name + i} className="dw-bar-row">
            <span className="dw-bar-name" title={name}>
              {name}
            </span>
            <div className="dw-bar-track">
              <div
                className="dw-bar-fill"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            <span className="dw-bar-val">{value}</span>
          </div>
        );
      })}
    </div>
  );
};

const DynamicRowList = ({ items }) => (
  <div className="dw-row-list">
    {items.slice(0, 8).map((it, i) => {
      const primary =
        pickField(it, NAME_KEYS, null) ??
        Object.values(it).find((v) => typeof v === "string") ??
        "Item";
      const secondary =
        it.notes ||
        it.status ||
        it.statusName ||
        it.reason ||
        it.date ||
        it.title ||
        "";
      return (
        <div key={i} className="crm-list-row">
          <Avatar name={String(primary)} />
          <div className="crm-list-body">
            <div className="crm-list-name">{String(primary)}</div>
            {secondary && (
              <div className="crm-list-meta">{String(secondary)}</div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const TodaysLeadsWidget = ({ tabs, onLeadClick, onViewAllLeads }) => {
  const [active, setActive] = useState(tabs[0]?.name ?? "");
  useEffect(() => {
    if (tabs[0]?.name && !active) setActive(tabs[0].name);
  }, [tabs]);
  const activeLeads =
    tabs.find((t) => t.name === (active || tabs[0]?.name))?.leads ?? [];
  return (
    <>
      <Tabs tabs={tabs} active={active || tabs[0]?.name} onChange={setActive} />
      <div className="dw-scroll">
        {activeLeads.length > 0 && onViewAllLeads && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "0 18px 10px",
            }}
          >
            <button
              className="crm-btn-ghost"
              onClick={() =>
                onViewAllLeads(active || tabs[0]?.name, activeLeads)
              }
            >
              View Full →
            </button>
          </div>
        )}
        {activeLeads.length === 0 ? (
          <EmptyState message="No leads in this status today" />
        ) : (
          activeLeads
            .slice(0, 30)
            .map((l) => (
              <LeadRow
                key={l.leadId || l.id}
                lead={l}
                showFollowup={active === "Followups"}
                onClick={onLeadClick}
              />
            ))
        )}
      </div>
    </>
  );
};

const RecentLeadsWidget = ({
  leads,
  onLeadClick,
  hasMore,
  loadingMore,
  onLoadMore,
  onViewAll,
}) => (
  <div className="dw-scroll">
    {onViewAll && (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "0 18px 10px",
        }}
      >
        <button className="crm-btn-ghost" onClick={onViewAll}>
          View Full →
        </button>
      </div>
    )}
    {leads.length === 0 ? (
      <EmptyState message="No recently updated leads found" />
    ) : (
      <>
        {leads.map((l) => (
          <div
            key={l.leadId || l.id}
            onClick={() => onLeadClick?.(l)}
            style={{ cursor: onLeadClick ? "pointer" : "default" }}
          >
            <LeadRow lead={l} />
          </div>
        ))}
        {hasMore && (
          <div style={{ padding: "10px 18px", textAlign: "center" }}>
            <button
              className="crm-btn-ghost"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </>
    )}
  </div>
);

const ExecWorkSummaryWidget = () => {
  const { user } = useContext(AuthContext);
  const [execSummary, setExecSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set default dates: 1st of the current month to Today
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");

  const [startDate, setStartDate] = useState(`${yyyy}-${mm}-01`);
  const [endDate, setEndDate] = useState(`${yyyy}-${mm}-${dd}`);

  const stableUser = useMemo(
    () => ({
      id: user?.id,
      accountType: user?.accountType,
      companyId: user?.companyId,
    }),
    [user?.id, user?.accountType, user?.companyId],
  );

  const companyId = useMemo(
    () =>
      isOwnerLevel(stableUser.accountType)
        ? stableUser.id
        : stableUser.companyId,
    [stableUser],
  );

  useEffect(() => {
    if (
      !companyId ||
      isFieldLevel(stableUser.accountType) ||
      isHR(stableUser.accountType)
    ) {
      setLoading(false);
      return;
    }
    if (!startDate || !endDate) return;

    setLoading(true);

    const targetId = isOwnerLevel(stableUser.accountType)
      ? companyId
      : stableUser.id;

    axios
      .get(
        `${API_URL}/api/leads/executive-work-summary/${targetId}?startDate=${startDate}&endDate=${endDate}`,
        { headers: authHeader() },
      )
      .then((r) => setExecSummary(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [companyId, stableUser.id, stableUser.accountType, startDate, endDate]);

  const colors = [
    "#673AB7",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Date Filters Control Bar - Ensures they are always visible */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          paddingBottom: "12px",
          marginBottom: "12px",
          borderBottom: "1px solid #f1f5f9",
          alignItems: "center",
        }}
      >
        <input
          type="date"
          className="crm-form-select"
          style={{ padding: "6px 10px", fontSize: "12px", flex: 1 }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          title="Start Date"
        />
        <span style={{ color: "#6b7280", fontSize: "12px", fontWeight: 500 }}>
          to
        </span>
        <input
          type="date"
          className="crm-form-select"
          style={{ padding: "6px 10px", fontSize: "12px", flex: 1 }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          title="End Date"
        />
      </div>

      {/* Widget Body */}
      <div className="dw-scroll" style={{ flex: 1 }}>
        {loading ? (
          <div className="crm-card-body">
            <Skeleton h={18} />
            <Skeleton h={18} />
          </div>
        ) : execSummary.length === 0 ? (
          <EmptyState message="No executive data available for this date range" />
        ) : (
          execSummary.map((exec, i) => (
            <div key={exec.executiveId || i} className="crm-team-row">
              <Avatar
                name={exec.executiveName || "?"}
                color={colors[i % colors.length]}
              />
              <div className="crm-list-body">
                <div className="crm-list-name">{exec.executiveName}</div>
                <div className="crm-list-meta">
                  Lead IDs assigned: {exec.leadIds?.length || 0}
                </div>
              </div>
              <div className="crm-team-nums">
                <span className="crm-team-num-big">
                  {exec.totalLeadsWorked || 0}
                </span>
                <span className="crm-team-num-small">leads worked</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
const TaskListWidget = ({ taskMap }) => {
  const tasks = taskMap?.today ?? (Array.isArray(taskMap) ? taskMap : []);
  return (
    <div className="dw-scroll">
      {tasks.length === 0 ? (
        <EmptyState message="No tasks due today" />
      ) : (
        tasks.map((task) => (
          <div key={task.taskId} className="crm-list-row">
            <div className="crm-task-dot" />
            <div className="crm-list-body">
              <div className="crm-list-name">{task.subject}</div>
              <div className="crm-list-meta">
                {task.assignedTo?.map((u) => u.name).join(", ")}
              </div>
            </div>
            {task.priority && (
              <span
                className="crm-pill"
                style={{
                  background:
                    task.priority === "High"
                      ? "#fee2e2"
                      : task.priority === "Medium"
                        ? "#fef9c3"
                        : "#f0fdf4",
                  color:
                    task.priority === "High"
                      ? "#dc2626"
                      : task.priority === "Medium"
                        ? "#ca8a04"
                        : "#16a34a",
                }}
              >
                {task.priority}
              </span>
            )}
          </div>
        ))
      )}
    </div>
  );
};

const ScheduleWidget = ({ schedule }) => {
  const reminders = schedule?.reminders?.today ?? [];
  const events = schedule?.events?.today ?? [];
  const tabs = [
    { name: "Reminders", count: reminders.length },
    { name: "Events", count: events.length },
  ];
  const [active, setActive] = useState("Reminders");
  const items = active === "Reminders" ? reminders : events;
  return (
    <>
      <Tabs tabs={tabs} active={active} onChange={setActive} />
      <div className="dw-scroll">
        {items.length === 0 ? (
          <EmptyState message={`No ${active.toLowerCase()} today`} />
        ) : (
          items.map((item) => (
            <div key={item.reminderId || item.eventId} className="crm-list-row">
              <div
                className="crm-task-dot"
                style={{
                  background: active === "Reminders" ? "#673AB7" : "#10b981",
                }}
              />
              <div className="crm-list-body">
                <div className="crm-list-name">
                  {active === "Reminders" ? item.message : item.title}
                </div>
                <div className="crm-list-meta">
                  {item.time || item.description}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

const AppointmentsWidget = () => {
  const { user } = useContext(AuthContext);
  const [filter, setFilter] = useState("today");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Safely grab user details
  const stableUser = useMemo(
    () => ({
      id: user?.id,
      accountType: user?.accountType,
      companyId: user?.companyId,
    }),
    [user?.id, user?.accountType, user?.companyId],
  );

  const companyId = useMemo(
    () =>
      isOwnerLevel(stableUser.accountType)
        ? stableUser.id
        : stableUser.companyId,
    [stableUser],
  );

  // Fetch appointments internally based on the toggle filter
  useEffect(() => {
    if (!stableUser.id || !companyId) return;

    setLoading(true);
    let url = "";

    if (isFieldLevel(stableUser.accountType)) {
      url = `${API_URL}/api/appointments/executive/${stableUser.id}?pageNumber=0&pageSize=10`;
    } else {
      url =
        filter === "today"
          ? `${API_URL}/api/appointments/company/${companyId}/today?pageNumber=0&pageSize=50`
          : `${API_URL}/api/appointments/company/${companyId}?pageNumber=0&pageSize=10`;
    }

    axios
      .get(url, { headers: authHeader() })
      .then((r) => {
        const data =
          r.data?.appointments ||
          r.data?.content ||
          (Array.isArray(r.data) ? r.data : []);
        setAppointments(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter, stableUser.id, companyId, stableUser.accountType]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* The Toggle Buttons */}
      {!isFieldLevel(stableUser.accountType) && (
        <div
          style={{
            paddingBottom: "10px",
            display: "flex",
            gap: "8px",
            borderBottom: "1px solid #f1f5f9",
            marginBottom: "10px",
          }}
        >
          <button
            className="crm-pill"
            style={
              filter === "all"
                ? {
                    background: "#0C4550",
                    color: "#fff",
                    border: "1px solid #0C4550",
                    cursor: "pointer",
                  }
                : {
                    background: "#f8fafc",
                    border: "1px solid #e5e9f0",
                    color: "#4b5563",
                    cursor: "pointer",
                  }
            }
            onClick={() => setFilter("all")}
          >
            All Appointments
          </button>
          <button
            className="crm-pill"
            style={
              filter === "today"
                ? {
                    background: "#0C4550",
                    color: "#fff",
                    border: "1px solid #0C4550",
                    cursor: "pointer",
                  }
                : {
                    background: "#f8fafc",
                    border: "1px solid #e5e9f0",
                    color: "#4b5563",
                    cursor: "pointer",
                  }
            }
            onClick={() => setFilter("today")}
          >
            Today's Appointments
          </button>
        </div>
      )}

      {/* The Appointments List */}
      <div className="dw-scroll" style={{ flex: 1 }}>
        {loading ? (
          <div className="crm-card-body">
            <Skeleton h={18} />
            <Skeleton h={18} />
          </div>
        ) : appointments.length === 0 ? (
          <EmptyState message="No appointments found" />
        ) : (
          appointments.map((apt, i) => (
            <div key={apt.appointmentId || i} className="crm-appt-card">
              <div className="crm-appt-header">
                <div className="crm-appt-dept">
                  <span className="crm-appt-label">Department:</span>{" "}
                  {apt.departmentName || "N/A"}
                </div>
                <StatusPill status={apt.status || "SCHEDULED"} />
              </div>
              <div className="crm-appt-row">
                <span className="crm-appt-label">Person:</span>{" "}
                {apt.personName || "N/A"}
              </div>
              <div className="crm-appt-datetime">
                <div className="crm-appt-dt-item">
                  <span className="crm-appt-label">Date:</span>{" "}
                  {apt.preferredDate || "-"}
                </div>
                <div className="crm-appt-dt-item">
                  <span className="crm-appt-label">Time:</span>{" "}
                  {apt.preferredTime || "-"}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const MonthlyActivityWidget = ({ data }) => {
  const month = data?.month || "";
  const sessions = data?.sessions || [];
  const attendance = data?.attendance || [];
  const chartData = useMemo(() => {
    if (!month) return null;
    const [yStr, mStr] = month.split("-");
    const daysInMonth = new Date(parseInt(yStr), parseInt(mStr), 0).getDate();
    const dates = Array.from(
      { length: daysInMonth },
      (_, i) => `${yStr}-${mStr}-${String(i + 1).padStart(2, "0")}`,
    );
    const toH = (t) => {
      if (!t) return 0;
      const p = t.split(":");
      return p.length >= 2 ? parseInt(p[0]) + parseInt(p[1]) / 60 : 0;
    };
    return {
      labels: dates.map((d) => d.split("-")[2]),
      datasets: [
        {
          label: "Active time",
          data: dates.map((d) =>
            toH(sessions.find((s) => s.date === d)?.totalTimeSpent),
          ),
          backgroundColor: "rgba(103,58,183,0.7)",
          borderColor: "#673AB7",
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: "Working hours",
          data: dates.map((d) =>
            toH(attendance.find((a) => a.date === d)?.workingHours),
          ),
          backgroundColor: "rgba(16,185,129,0.7)",
          borderColor: "#10b981",
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [month, sessions, attendance]);

  if (!chartData) return <EmptyState message="No activity data" />;
  return (
    <div style={{ height: 220 }}>
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
        }}
      />
    </div>
  );
};

const LeadTrendWidget = () => {
  const { user } = useContext(AuthContext);
  const [range, setRange] = useState("week");
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);

  const stableUser = useMemo(
    () => ({
      id: user?.id,
    }),
    [user?.id],
  );

  useEffect(() => {
    if (!stableUser.id) return;
    setLoading(true);

    // Fetch using the new endpoint shape and passing the dynamic range
    axios
      .get(
        `${API_URL}/api/dashboard/data/LEAD_TREND?userId=${stableUser.id}&range=${range}`,
        { headers: authHeader() },
      )
      .then((r) => setTrendData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [stableUser.id, range]);

  // Format data specifically for the Line chart
  const chartData = useMemo(() => {
    if (!trendData || !trendData.points || trendData.points.length === 0)
      return null;

    return {
      labels: trendData.points.map((p) => p.label),
      datasets: [
        {
          label: "Leads Created",
          data: trendData.points.map((p) => p.count),
          backgroundColor: "rgba(103, 58, 183, 0.15)",
          borderColor: "#673AB7",
          borderWidth: 2,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#673AB7",
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [trendData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, font: { size: 10 } },
        border: { dash: [4, 4] },
        grid: { color: "#f1f5f9" },
      },
    },
    interaction: { intersect: false, mode: "index" },
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "12px",
          marginBottom: "12px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <select
          className="crm-form-select"
          style={{ width: "auto", padding: "4px 10px", fontSize: "12px" }}
          value={range}
          onChange={(e) => setRange(e.target.value)}
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="all">All Time</option>
        </select>

        {/* Display responseTimeMs metric */}
        {trendData?.responseTimeMs && !loading && (
          <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 500 }}>
            Query time: {trendData.responseTimeMs}ms
          </span>
        )}
      </div>

      {/* Chart Area */}
      <div style={{ flex: 1, minHeight: 180, position: "relative" }}>
        {loading ? (
          <div className="crm-center">
            <div className="crm-spinner" />
          </div>
        ) : !chartData ? (
          <EmptyState message="No trend data available for this range" />
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};
const ORG_ROLE_META = {
  COMPANY: { label: "Owner", color: "#0C4550" },
  VICE_PRESIDENT: { label: "Vice President", color: "#673AB7" },
  MANAGER: { label: "Manager", color: "#0ea5e9" },
  TEAM_LEAD: { label: "Team Lead", color: "#f59e0b" },
  EXECUTIVE: { label: "Executive", color: "#10b981" },
  HR: { label: "HR", color: "#ec4899" },
};

const ORG_CHILD_LABEL = {
  VICE_PRESIDENT: ["manager", "managers"],
  MANAGER: ["team lead", "team leads"],
  TEAM_LEAD: ["executive", "executives"],
};

const ORG_ROLE_ORDER = ["VICE_PRESIDENT", "MANAGER", "TEAM_LEAD", "EXECUTIVE"];

const ORG_METRIC_SOURCES = [
  "TEAM_PERFORMANCE",
  "EXECUTIVE_LEADERBOARD",
  "AI_CALL_RATING",
  "TEAM_ATTENDANCE",
];

const orgTypeKey = (t) => String(t || "").toUpperCase();

const orgRoleMeta = (t) => {
  const key = orgTypeKey(t);
  if (ORG_ROLE_META[key]) return ORG_ROLE_META[key];
  const label = key
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
  return { label: label || "Member", color: "#6b7280" };
};

const orgFullName = (n) =>
  `${n.fname ?? ""} ${n.lname ?? ""}`.trim() || n.email || "Unnamed";

const orgIsActive = (n) => (n.active ?? n.isActive) !== false;

const normalizeOrgRoots = (data) => {
  if (Array.isArray(data)) return data;
  if (isPlainObject(data) && data.id) return [data];
  return [];
};

const collectExpandableIds = (nodes, out = new Set()) => {
  nodes.forEach((n) => {
    if (n.children?.length) {
      out.add(n.id);
      collectExpandableIds(n.children, out);
    }
  });
  return out;
};

const summarizeOrg = (nodes) => {
  const acc = { total: 0, active: 0, byRole: {} };
  const walk = (list) => {
    list.forEach((n) => {
      acc.total += 1;
      if (orgIsActive(n)) acc.active += 1;
      const key = orgTypeKey(n.accountType);
      acc.byRole[key] = (acc.byRole[key] || 0) + 1;
      walk(n.children ?? []);
    });
  };
  walk(nodes);
  return acc;
};

const orgMatches = (n, q) =>
  orgFullName(n).toLowerCase().includes(q) ||
  String(n.email || "")
    .toLowerCase()
    .includes(q) ||
  orgRoleMeta(n.accountType).label.toLowerCase().includes(q);

const filterOrgTree = (nodes, q) =>
  nodes.reduce((acc, n) => {
    if (orgMatches(n, q)) {
      acc.push(n);
      return acc;
    }
    const kids = filterOrgTree(n.children ?? [], q);
    if (kids.length) acc.push({ ...n, children: kids });
    return acc;
  }, []);

const rowsToMap = (result, idKey) => {
  if (result.status !== "fulfilled" || !Array.isArray(result.value))
    return null;
  return result.value.reduce((acc, row) => {
    if (row && row[idKey]) acc[row[idKey]] = row;
    return acc;
  }, {});
};

const EMPTY_ORG_METRICS = {
  loaded: false,
  leads: null,
  actions: null,
  ratings: null,
  attendance: null,
};

const useOrgMetrics = (userId) => {
  const [metrics, setMetrics] = useState(EMPTY_ORG_METRICS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    const load = (key) =>
      axios
        .get(`${API_URL}/api/dashboard/data/${key}?userId=${userId}`, {
          headers: authHeader(),
        })
        .then((r) => r.data);
    Promise.allSettled(ORG_METRIC_SOURCES.map(load)).then(
      ([perf, acts, ratings, att]) => {
        if (cancelled) return;
        setMetrics({
          loaded: true,
          leads: rowsToMap(perf, "userId"),
          actions: rowsToMap(acts, "executiveId"),
          ratings: rowsToMap(ratings, "executiveId"),
          attendance: rowsToMap(att, "userId"),
        });
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { metrics, loading };
};

const buildOrgRollups = (nodes, leadsMap) => {
  const out = {};
  const walk = (n) => {
    const own = leadsMap?.[n.id];
    let leads = own ? own.totalLeads : 0;
    let conv = own ? own.conversions : 0;
    (n.children ?? []).forEach((c) => {
      const r = walk(c);
      leads += r.leads;
      conv += r.conv;
    });
    out[n.id] = { leads, conv };
    return out[n.id];
  };
  nodes.forEach(walk);
  return out;
};

const OrgStat = ({ label, value, sub, title }) => (
  <span className="org-stat" title={title}>
    <span className="org-stat-label">{label}</span>
    <b>{value}</b>
    {sub && <span className="org-stat-sub">{sub}</span>}
  </span>
);

const OrgNodeRow = memo(
  ({ node, expanded, onToggle, forceOpen, metrics, rollups }) => {
    const kids = node.children ?? [];
    const hasKids = kids.length > 0;
    const open = hasKids && (forceOpen || expanded.has(node.id));
    const typeKey = orgTypeKey(node.accountType);
    const meta = orgRoleMeta(node.accountType);
    const name = orgFullName(node);
    const active = orgIsActive(node);
    const showMetrics = typeKey !== "EXECUTIVE";
    const direct = node.directReporteeCount ?? kids.length;
    const total = node.totalTeamSize ?? 0;
    const childLabel = ORG_CHILD_LABEL[typeKey];
    const directLabel = childLabel
      ? childLabel[direct === 1 ? 0 : 1]
      : "direct";

    const lead = metrics.leads?.[node.id];
    const roll = rollups[node.id];
    const actions = metrics.actions
      ? (metrics.actions[node.id]?.totalActions ?? 0)
      : null;
    const rating = metrics.ratings?.[node.id];
    const attendance = metrics.attendance?.[node.id];
    const showTeam = Boolean(metrics.leads) && hasKids && roll;
    const rate =
      lead && lead.totalLeads > 0
        ? Math.round((lead.conversions / lead.totalLeads) * 100)
        : 0;
    const hasPerf = Boolean(
      lead || showTeam || actions !== null || rating || attendance,
    );

    return (
      <div className="org-node">
        <div className={`org-row ${active ? "" : "org-row--inactive"}`}>
          <button
            type="button"
            className={`org-toggle ${open ? "org-toggle--open" : ""}`}
            disabled={!hasKids}
            aria-expanded={hasKids ? open : undefined}
            aria-label={
              hasKids ? `${open ? "Collapse" : "Expand"} ${name}` : undefined
            }
            onClick={() => onToggle(node.id)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
          <Avatar name={name} color={meta.color} />
          <div className="crm-list-body">
            <div className="org-name-line">
              <span className="crm-list-name">{name}</span>
              <span
                className="crm-pill"
                style={{ background: meta.color + "22", color: meta.color }}
              >
                {meta.label}
              </span>
            </div>
            {node.email && <div className="crm-list-meta">{node.email}</div>}
            {hasPerf && (
              <div className="org-perf">
                {attendance && <StatusPill status={attendance.status} />}
                {lead && (
                  <OrgStat
                    label="Leads"
                    value={lead.totalLeads}
                    title="Leads currently assigned to this person"
                  />
                )}
                {lead && (
                  <OrgStat
                    label="Converted"
                    value={lead.conversions}
                    sub={`${rate}%`}
                    title="Leads converted by this person"
                  />
                )}
                {showTeam && (
                  <OrgStat
                    label="Team leads"
                    value={roll.leads}
                    title="Leads across this person and everyone below them"
                  />
                )}
                {showTeam && (
                  <OrgStat
                    label="Team converted"
                    value={roll.conv}
                    title="Conversions across this person and everyone below them"
                  />
                )}
                {actions !== null && (
                  <OrgStat label="Actions today" value={actions} />
                )}
                {rating && (
                  <OrgStat
                    label="Call rating"
                    value={Number(rating.averageRating ?? 0).toFixed(1)}
                    sub={`${rating.totalRatedCalls ?? 0} calls`}
                  />
                )}
              </div>
            )}
          </div>
          {showMetrics && (
            <div className="org-metrics">
              <span>
                <b>{direct}</b> {directLabel}
              </span>
              <span>
                <b>{total}</b> in team
              </span>
            </div>
          )}
          <span
            className={`org-status ${active ? "org-status--on" : "org-status--off"}`}
            title={active ? "Active" : "Inactive"}
            role="img"
            aria-label={active ? "Active" : "Inactive"}
          />
        </div>
        {open && (
          <div className="org-children">
            {kids.map((child, i) => (
              <OrgNodeRow
                key={child.id ?? i}
                node={child}
                expanded={expanded}
                onToggle={onToggle}
                forceOpen={forceOpen}
                metrics={metrics}
                rollups={rollups}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

const OrgHierarchyWidget = ({ data }) => {
  const { user } = useContext(AuthContext);
  const roots = useMemo(() => normalizeOrgRoots(data), [data]);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(
    () => new Set(roots.filter((r) => r.children?.length).map((r) => r.id)),
  );
  const { metrics, loading: loadingMetrics } = useOrgMetrics(user?.id);

  useEffect(() => {
    setExpanded(
      new Set(roots.filter((r) => r.children?.length).map((r) => r.id)),
    );
  }, [roots]);

  const summary = useMemo(() => summarizeOrg(roots), [roots]);
  const rollups = useMemo(
    () => buildOrgRollups(roots, metrics.leads),
    [roots, metrics.leads],
  );

  const totals = useMemo(
    () =>
      roots.reduce(
        (acc, r) => {
          const x = rollups[r.id];
          if (x) {
            acc.leads += x.leads;
            acc.conv += x.conv;
          }
          return acc;
        },
        { leads: 0, conv: 0 },
      ),
    [roots, rollups],
  );

  const roleKeys = useMemo(
    () =>
      Object.keys(summary.byRole).sort((a, b) => {
        const ia = ORG_ROLE_ORDER.indexOf(a);
        const ib = ORG_ROLE_ORDER.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      }),
    [summary],
  );

  const q = query.trim().toLowerCase();
  const visibleRoots = useMemo(
    () => (q ? filterOrgTree(roots, q) : roots),
    [roots, q],
  );

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(
    () => setExpanded(collectExpandableIds(roots)),
    [roots],
  );
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  if (roots.length === 0) {
    return (
      <EmptyState message="No reporting structure found for your account" />
    );
  }

  const inactive = summary.total - summary.active;
  const anyMetrics = Boolean(
    metrics.leads || metrics.actions || metrics.ratings || metrics.attendance,
  );

  return (
    <>
      <style>{ORG_CSS}</style>
      <div className="org-wrap">
        <div className="org-summary">
          <div className="org-chip">
            <span className="org-chip-val">{summary.total}</span>
            <span className="org-chip-label">People</span>
          </div>
          {roleKeys.map((key) => {
            const meta = orgRoleMeta(key);
            const count = summary.byRole[key];
            return (
              <div key={key} className="org-chip">
                <span
                  className="org-chip-dot"
                  style={{ background: meta.color }}
                />
                <span className="org-chip-val">{count}</span>
                <span className="org-chip-label">
                  {meta.label}
                  {count === 1 ? "" : "s"}
                </span>
              </div>
            );
          })}
          {inactive > 0 && (
            <div className="org-chip">
              <span
                className="org-chip-dot"
                style={{ background: "#cbd5e1" }}
              />
              <span className="org-chip-val">{inactive}</span>
              <span className="org-chip-label">Inactive</span>
            </div>
          )}
          {metrics.leads && (
            <>
              <div className="org-chip org-chip--metric">
                <span className="org-chip-val">{totals.leads}</span>
                <span className="org-chip-label">Leads</span>
              </div>
              <div className="org-chip org-chip--metric">
                <span className="org-chip-val">{totals.conv}</span>
                <span className="org-chip-label">Converted</span>
              </div>
            </>
          )}
        </div>

        <div className="org-toolbar">
          <input
            type="text"
            className="crm-form-select org-search"
            placeholder="Search by name, email or role"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" className="crm-btn-ghost" onClick={expandAll}>
            Expand all
          </button>
          <button type="button" className="crm-btn-ghost" onClick={collapseAll}>
            Collapse all
          </button>
          {loadingMetrics && (
            <span className="org-loading">Loading performance</span>
          )}
        </div>

        {metrics.loaded && !anyMetrics && (
          <div className="org-note">
            Lead and activity numbers aren't available for your account.
          </div>
        )}

        <div className="dw-scroll org-tree">
          {visibleRoots.length === 0 ? (
            <EmptyState message="Nobody matches your search" />
          ) : (
            visibleRoots.map((root, i) => (
              <OrgNodeRow
                key={root.id ?? i}
                node={root}
                expanded={expanded}
                onToggle={toggle}
                forceOpen={q.length > 0}
                metrics={metrics}
                rollups={rollups}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

const ORG_CSS = `
  .org-wrap { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
  .org-summary { display: flex; flex-wrap: wrap; gap: 8px; }
  .org-chip { display: inline-flex; align-items: center; gap: 6px; background: #f8fafc; border: 1px solid #eef1f6; border-radius: 10px; padding: 6px 10px; }
  .org-chip--metric { background: #f5f0ff; border-color: #e9dcff; }
  .org-chip-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .org-chip-val { font-size: 16px; font-weight: 800; color: #1a1f36; line-height: 1; font-variant-numeric: tabular-nums; }
  .org-chip-label { font-size: 11px; font-weight: 600; color: #6b7280; }
  .org-toolbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .org-search { flex: 1; width: auto; min-width: 160px; padding: 6px 10px; font-size: 12px; }
  .org-loading { font-size: 11px; font-weight: 500; color: #9ca3af; }
  .org-note { font-size: 11px; color: #9ca3af; }
  .org-tree { max-height: 460px; padding-right: 4px; }
  .org-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 8px 8px 4px; border-radius: 10px; transition: background .1s; }
  .org-row:hover { background: #f8fafc; }
  .org-row--inactive .crm-list-name, .org-row--inactive .crm-list-meta { opacity: .55; }
  .org-toggle { width: 22px; height: 22px; margin-top: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: none; border-radius: 6px; background: transparent; color: #6b7280; cursor: pointer; padding: 0; }
  .org-toggle:hover:not(:disabled) { background: #f3e8ff; color: #673AB7; }
  .org-toggle:disabled { opacity: 0; cursor: default; }
  .org-toggle svg { transition: transform .15s; }
  .org-toggle--open svg { transform: rotate(90deg); }
  .org-name-line { display: flex; align-items: center; gap: 8px; min-width: 0; }
  .org-name-line .crm-list-name { min-width: 0; }
  .org-perf { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 6px; }
  .org-stat { display: inline-flex; align-items: baseline; gap: 5px; background: #f8fafc; border: 1px solid #eef1f6; border-radius: 8px; padding: 3px 8px; font-size: 11px; white-space: nowrap; }
  .org-stat-label { color: #9ca3af; font-weight: 500; }
  .org-stat b { color: #1a1f36; font-weight: 700; font-variant-numeric: tabular-nums; }
  .org-stat-sub { color: #673AB7; font-weight: 600; }
  .org-metrics { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; margin-top: 2px; flex-shrink: 0; font-size: 11px; font-weight: 500; color: #9ca3af; white-space: nowrap; }
  .org-metrics b { color: #1a1f36; font-weight: 700; }
  .org-status { width: 8px; height: 8px; margin-top: 13px; border-radius: 50%; flex-shrink: 0; }
  .org-status--on { background: #16a34a; }
  .org-status--off { background: #cbd5e1; }
  .org-children { margin-left: 15px; padding-left: 12px; border-left: 1px solid #e5e9f0; }
`;
const LEAD_ACTION_CSS = `
  .la-totals { display: flex; gap: 8px; padding: 12px 18px 6px; }
  .la-total { flex: 1; background: #f8fafc; border: 1px solid #eef1f6; border-radius: 8px; padding: 8px 10px; display: flex; flex-direction: column; }
  .la-total b { font-size: 20px; color: #1a1f36; font-variant-numeric: tabular-nums; }
  .la-total span { font-size: 11px; color: #9ca3af; font-weight: 500; }
  .la-total--warn b { color: #d97706; }
  .la-exec:hover { background: #f8fafc; }
  .la-bar { height: 4px; background: #eef1f6; border-radius: 4px; margin: 5px 0 3px; overflow: hidden; }
  .la-bar-fill { height: 100%; background: #673AB7; }
  .la-remaining { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
  .la-remaining b { font-size: 16px; color: #16a34a; font-variant-numeric: tabular-nums; }
  .la-remaining--due b { color: #d97706; }
  .la-remaining span { font-size: 10px; color: #9ca3af; }
  .la-section { padding: 10px 18px 4px; font-size: 12px; font-weight: 600; color: #1a1f36; }
  .la-reason { font-size: 11px; color: #6b7280; margin-top: 2px; }
  .la-status { font-size: 10px; font-weight: 600; padding: 2px 8px; border: 1px solid; border-radius: 10px; flex-shrink: 0; white-space: nowrap; }
  .la-detail-head { display: flex; align-items: center; gap: 10px; padding: 10px 18px; }
  .la-detail-title { font-size: 13px; font-weight: 600; color: #1a1f36; }
  .la-note { padding: 6px 18px; font-size: 11px; color: #9ca3af; }
  .la-overdue { color: #dc2626; font-weight: 600; }
  .la-fu-edit { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 0 18px 12px 64px; }
  .la-fu-edit input { border: 1px solid #e5e9f0; border-radius: 8px; padding: 6px 10px; font-size: 12px; }
  .la-miss-count { color: #dc2626; font-weight: 700; }
`;

const LA_RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
];

const LeadActionReportWidget = ({ onLeadClick }) => {
  const { user } = useContext(AuthContext);
  const [range, setRange] = useState("today");
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get(`${API_URL}/api/dashboard/data/LEAD_ACTION_REPORT`, {
        params: { userId: user.id, range },
        headers: authHeader(),
      })
      .then((r) => {
        if (!cancelled) setReport(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, range]);

  useEffect(() => {
    if (!user?.id || !selected) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/dashboard/data/LEAD_ACTION_REPORT`, {
        params: {
          userId: user.id,
          range,
          targetUserId: selected.executiveId,
          limit: 100,
        },
        headers: authHeader(),
      })
      .then((r) => {
        if (!cancelled) setDetail(r.data);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [user?.id, range, selected]);

  const changeRange = (key) => {
    setSelected(null);
    setDetail(null);
    setRange(key);
  };

  const renderLead = (l) => (
    <div
      key={l.leadId}
      className="crm-list-row"
      onClick={onLeadClick ? () => onLeadClick(l) : undefined}
      style={onLeadClick ? { cursor: "pointer" } : undefined}
    >
      <Avatar name={l.name || "?"} />
      <div className="crm-list-body">
        <div className="crm-list-name">{l.name}</div>
        <div className="crm-list-meta">
          {l.phone}
          {l.sourceName ? `  ${l.sourceName}` : ""}
        </div>
        {l.reason && <div className="la-reason">{l.reason}</div>}
      </div>
      {l.statusName && (
        <span
          className="la-status"
          style={{
            borderColor: l.statusColor || "#cbd5e1",
            color: l.statusColor || "#6b7280",
          }}
        >
          {l.statusName}
        </span>
      )}
    </div>
  );

  const skeleton = (
    <div className="dw-card-loading">
      <Skeleton h={16} />
      <Skeleton h={16} />
      <Skeleton h={16} />
    </div>
  );

  let body = null;
  if (loading) body = skeleton;
  else if (error) body = <EmptyState message="Couldn't load this report" />;
  else if (report && selected) {
    body = (
      <>
        <div className="la-detail-head">
          <button
            className="crm-btn-ghost"
            onClick={() => {
              setSelected(null);
              setDetail(null);
            }}
          >
            Back
          </button>
          <div className="la-detail-title">{selected.executiveName}</div>
        </div>
        {!detail ? (
          skeleton
        ) : (
          <div className="dw-scroll">
            <div className="la-section">Remaining ({detail.remaining})</div>
            {detail.remainingLeads.length === 0 ? (
              <EmptyState message="Every lead has been updated" />
            ) : (
              detail.remainingLeads.map(renderLead)
            )}
            {detail.remaining > detail.remainingLeads.length && (
              <div className="la-note">
                Showing the first {detail.remainingLeads.length} of{" "}
                {detail.remaining}
              </div>
            )}
            <div className="la-section">Updated ({detail.updated})</div>
            {detail.updatedLeads.length === 0 ? (
              <EmptyState message="Nothing updated yet" />
            ) : (
              detail.updatedLeads.map(renderLead)
            )}
            {detail.updated > detail.updatedLeads.length && (
              <div className="la-note">
                Showing the first {detail.updatedLeads.length} of{" "}
                {detail.updated}
              </div>
            )}
          </div>
        )}
      </>
    );
  } else if (report) {
    body = (
      <>
        <div className="la-totals">
          <div className="la-total">
            <b>{report.received}</b>
            <span>received</span>
          </div>
          <div className="la-total">
            <b>{report.updated}</b>
            <span>updated</span>
          </div>
          <div className="la-total la-total--warn">
            <b>{report.remaining}</b>
            <span>remaining</span>
          </div>
        </div>
        {report.executives.length === 0 ? (
          <EmptyState message="No new leads in this period" />
        ) : (
          <div className="dw-scroll">
            {report.executives.map((e) => {
              const pct = e.received
                ? Math.round((e.updated / e.received) * 100)
                : 0;
              return (
                <div
                  key={e.executiveId}
                  className="crm-list-row la-exec"
                  onClick={() => setSelected(e)}
                  style={{ cursor: "pointer" }}
                >
                  <Avatar name={e.executiveName || "?"} />
                  <div className="crm-list-body">
                    <div className="crm-list-name">{e.executiveName}</div>
                    <div className="la-bar">
                      <div className="la-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="crm-list-meta">
                      {e.updated} of {e.received} updated (status{" "}
                      {e.statusUpdated}, reason {e.reasonUpdated})
                    </div>
                  </div>
                  <div
                    className={`la-remaining ${e.remaining > 0 ? "la-remaining--due" : ""}`}
                  >
                    <b>{e.remaining}</b>
                    <span>remaining</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <style>{LEAD_ACTION_CSS}</style>
      <div className="crm-tabs">
        {LA_RANGES.map((r) => (
          <button
            key={r.key}
            className={`crm-tab ${range === r.key ? "crm-tab--active" : ""}`}
            onClick={() => changeRange(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>
      {body}
    </>
  );
};

const PERF_CSS = `
  .pf-totals { display: grid; grid-template-columns: repeat(auto-fit, minmax(72px, 1fr)); gap: 6px; padding: 8px 14px 4px; }
  .pf-total { background: #f8fafc; border: 1px solid #eef1f6; border-radius: 6px; padding: 5px 8px; display: flex; flex-direction: column; gap: 1px; }
  .pf-total b { font-size: 14px; font-weight: 700; color: #1a1f36; line-height: 1.2; font-variant-numeric: tabular-nums; }
  .pf-total span { font-size: 9.5px; color: #9ca3af; font-weight: 500; letter-spacing: .01em; }

  .pf-row { padding: 6px 14px; gap: 9px; }
  .pf-row:hover { background: #f8fafc; }
  .pf-row .crm-avatar { width: 26px; height: 26px; border-radius: 8px; font-size: 9px; }
  .pf-row .crm-list-name { font-size: 11.5px; }
  .pf-row .crm-list-meta { font-size: 10px; margin-top: 1px; }

  .pf-bar { height: 3px; background: #eef1f6; border-radius: 3px; margin: 3px 0 2px; overflow: hidden; }
  .pf-bar-fill { height: 100%; background: #673AB7; border-radius: 3px; }

  .pf-side { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
  .pf-side b { font-size: 13px; font-weight: 700; color: #1a1f36; line-height: 1.1; font-variant-numeric: tabular-nums; }
  .pf-side span { font-size: 9px; color: #9ca3af; }

  .pf-detail-head { display: flex; align-items: center; gap: 8px; padding: 7px 14px; }
  .pf-detail-head .crm-btn-ghost { padding: 3px 9px; font-size: 10.5px; border-radius: 6px; }
  .pf-detail-title { font-size: 12px; font-weight: 600; color: #1a1f36; }

  .pf-day { display: grid; grid-template-columns: 1.3fr .6fr .7fr .6fr 1fr; gap: 4px; padding: 5px 14px; border-bottom: 1px solid #f4f6fa; font-size: 10.5px; color: #1a1f36; font-variant-numeric: tabular-nums; }
  .pf-day--head { color: #9ca3af; font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #eef1f6; }
  .pf-miss { color: #dc2626; font-weight: 600; }

  .pf-note { padding: 3px 14px; font-size: 10px; color: #9ca3af; line-height: 1.4; }

  .pf-nav { display: flex; align-items: center; gap: 6px; padding: 6px 14px 0; }
  .pf-nav .crm-btn-ghost { padding: 3px 9px; font-size: 10.5px; border-radius: 6px; }
  .pf-nav-label { flex: 1; text-align: center; font-size: 11px; font-weight: 600; color: #1a1f36; }
  .pf-date { border: 1px solid #e5e7eb; border-radius: 6px; padding: 2px 6px; font-size: 10px; color: #1a1f36; font-family: inherit; }

  .pf-role { margin-left: 5px; padding: 1px 5px; border-radius: 10px; background: #ede7f6; color: #673AB7; font-size: 9px; font-weight: 600; }

  .pf-tabs .crm-tab { padding: 7px 10px; font-size: 11px; }
`;

const PERF_PERIODS = [
  { key: "day", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

const fmtTalk = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(r).padStart(2, "0")}s`;
  return `${r}s`;
};

const fmtPerfDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const shiftIso = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const nextPeriodStart = (iso, period) => {
  if (period === "day") return shiftIso(iso, 1);
  if (period === "week") return shiftIso(iso, 7);
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1, 1);
  return d.toISOString().slice(0, 10);
};

const todayIso = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const fmtPerfRole = (role) =>
  String(role || "")
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const ExecPerformanceWidget = () => {
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState("day");
  const [anchor, setAnchor] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get(`${API_URL}/api/dashboard/data/EXEC_PERFORMANCE`, {
        params: {
          userId: user.id,
          range: period,
          ...(anchor ? { month: anchor } : {}),
        },
        headers: authHeader(),
      })
      .then((r) => {
        if (!cancelled) setReport(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, period, anchor]);

  const changePeriod = (key) => {
    setSelectedId(null);
    setAnchor(null);
    setPeriod(key);
  };

  const nextStart = report ? nextPeriodStart(report.from, report.period) : null;
  const canGoNext = !!nextStart && nextStart <= todayIso();

  const goPrev = () => {
    if (!report) return;
    setSelectedId(null);
    setAnchor(shiftIso(report.from, -1));
  };

  const goNext = () => {
    if (!canGoNext) return;
    setSelectedId(null);
    setAnchor(nextStart);
  };

  const pickDate = (value) => {
    if (!value) return;
    setSelectedId(null);
    setAnchor(value);
  };

  const rangeLabel = report
    ? report.period === "day"
      ? fmtPerfDate(report.from)
      : `${fmtPerfDate(report.from)} - ${fmtPerfDate(report.to)}`
    : "";

  const renderDetail = (e, showBack) => (
    <>
      <div className="pf-detail-head">
        {showBack && (
          <button className="crm-btn-ghost" onClick={() => setSelectedId(null)}>
            Back
          </button>
        )}
        <div className="pf-detail-title">
          {showBack ? (
            <>
              {e.executiveName}
              {e.role && <span className="pf-role">{fmtPerfRole(e.role)}</span>}
            </>
          ) : (
            "My performance"
          )}
        </div>
      </div>
      <div className="pf-totals">
        <div className="pf-total">
          <b>{e.totalCalls}</b>
          <span>calls made</span>
        </div>
        <div className="pf-total">
          <b>{e.connectRate}%</b>
          <span>answered</span>
        </div>
        <div className="pf-total">
          <b>{fmtTalk(e.totalTalkSeconds)}</b>
          <span>talk time</span>
        </div>
        <div className="pf-total">
          <b>{fmtTalk(e.avgTalkSeconds)}</b>
          <span>avg talk time</span>
        </div>
      </div>
      {e.totalCalls === 0 && (
        <div className="pf-note">No calls recorded in this period</div>
      )}
      <div className="pf-note">
        Outgoing {e.outgoing} · Incoming {e.incoming} · Missed {e.missed} ·
        Declined {e.declined}
        {report.period !== "day" ? ` · Active days ${e.activeDays}` : ""}
      </div>
      {e.daily.length > 1 && (
        <div className="dw-scroll">
          <div className="pf-day pf-day--head">
            <span>Date</span>
            <span>Calls</span>
            <span>Answered</span>
            <span>Missed</span>
            <span>Talk time</span>
          </div>
          {e.daily.map((d) => (
            <div key={d.date} className="pf-day">
              <span>{fmtPerfDate(d.date)}</span>
              <span>{d.totalCalls}</span>
              <span>{d.answered}</span>
              <span className={d.missed > 0 ? "pf-miss" : ""}>{d.missed}</span>
              <span>{fmtTalk(d.talkSeconds)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );

  let body = null;
  if (loading) {
    body = (
      <div className="dw-card-loading">
        <Skeleton h={16} />
        <Skeleton h={16} />
        <Skeleton h={16} />
      </div>
    );
  } else if (error) {
    body = <EmptyState message="Couldn't load performance" />;
  } else if (report) {
    const rows = report.executives || [];
    if (!report.teamView) {
      const own = rows.find((r) => r.executiveId === user?.id) || rows[0];
      body = own ? (
        renderDetail(own, false)
      ) : (
        <EmptyState message="No call activity in this period" />
      );
    } else {
      const selectedRow = selectedId
        ? rows.find((r) => r.executiveId === selectedId)
        : null;
      if (selectedRow) {
        body = renderDetail(selectedRow, true);
      } else {
        const maxCalls = Math.max(1, ...rows.map((r) => r.totalCalls));
        body = (
          <>
            <div className="pf-totals">
              <div className="pf-total">
                <b>{report.summary.totalCalls}</b>
                <span>calls made</span>
              </div>
              <div className="pf-total">
                <b>{report.summary.connectRate}%</b>
                <span>answered</span>
              </div>
              <div className="pf-total">
                <b>{fmtTalk(report.summary.totalTalkSeconds)}</b>
                <span>talk time</span>
              </div>
              <div className="pf-total">
                <b>{fmtTalk(report.summary.avgTalkSeconds)}</b>
                <span>avg talk time</span>
              </div>
            </div>
            {report.summary.totalCalls === 0 && (
              <div className="pf-note">No calls recorded in this period</div>
            )}
            {rows.length === 0 ? (
              <EmptyState message="No executives to show" />
            ) : (
              <div className="dw-scroll">
                {rows.map((r) => (
                  <div
                    key={r.executiveId}
                    className="crm-list-row pf-row"
                    onClick={() => setSelectedId(r.executiveId)}
                    style={{ cursor: "pointer" }}
                  >
                    <Avatar name={r.executiveName || "?"} />
                    <div className="crm-list-body">
                      <div className="crm-list-name">
                        {r.executiveName}
                        {r.role && (
                          <span className="pf-role">{fmtPerfRole(r.role)}</span>
                        )}
                      </div>
                      <div className="pf-bar">
                        <div
                          className="pf-bar-fill"
                          style={{ width: `${(r.totalCalls / maxCalls) * 100}%` }}
                        />
                      </div>
                      <div className="crm-list-meta">
                        {r.answered} answered,{" "}
                        <span className={r.missed > 0 ? "pf-miss" : ""}>
                          {r.missed} missed
                        </span>
                        , avg talk {fmtTalk(r.avgTalkSeconds)}
                      </div>
                    </div>
                    <div className="pf-side">
                      <b>{r.totalCalls}</b>
                      <span>calls</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      }
    }
  }

  return (
    <>
      <style>{PERF_CSS}</style>
      <div className="crm-tabs pf-tabs">
        {PERF_PERIODS.map((p) => (
          <button
            key={p.key}
            className={`crm-tab ${period === p.key ? "crm-tab--active" : ""}`}
            onClick={() => changePeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="pf-nav">
        <button className="crm-btn-ghost" onClick={goPrev} disabled={!report}>
          Prev
        </button>
        <span className="pf-nav-label">{rangeLabel}</span>
        <button className="crm-btn-ghost" onClick={goNext} disabled={!canGoNext}>
          Next
        </button>
        <input
          type="date"
          className="pf-date"
          max={todayIso()}
          value={report?.from || ""}
          onChange={(ev) => pickDate(ev.target.value)}
        />
      </div>
      {body}
    </>
  );
};

const FollowUpsDueWidget = ({ onLeadClick }) => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Missed");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [extending, setExtending] = useState(null);
  const [extDate, setExtDate] = useState("");
  const [extNotes, setExtNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/dashboard/data/FOLLOWUPS_DUE`, {
        params: { userId: user.id, limit: 100 },
        headers: authHeader(),
      })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, reload]);

  const saveExtend = (f) => {
    if (!extDate) return;
    setSaving(true);
    axios
      .post(
        `${API_URL}/api/leads/${f.leadId}/followups`,
        {
          nextFollowUpDate: extDate,
          notes: extNotes.trim() || "Follow-up extended",
          followedById: f.followedById || user.id,
          followedByName: f.followedByName || "",
          createdById: user.id,
        },
        { headers: authHeader() },
      )
      .then(() => {
        setExtending(null);
        setExtDate("");
        setExtNotes("");
        setReload((n) => n + 1);
      })
      .catch(console.error)
      .finally(() => setSaving(false));
  };

  const whenLabel = (f) => {
    if (!f.nextFollowUpDate) return "";
    const d = new Date(
      f.nextFollowUpTime
        ? `${f.nextFollowUpDate}T${f.nextFollowUpTime}`
        : f.nextFollowUpDate,
    );
    return (
      d.toLocaleDateString("en-IN") +
      (f.nextFollowUpTime
        ? " " +
          d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
        : "")
    );
  };

  const renderItem = (f) => (
    <div key={f.followUpId || f.leadId}>
      <div
        className="crm-list-row"
        onClick={
          onLeadClick
            ? () => onLeadClick({ leadId: f.leadId, name: f.leadName })
            : undefined
        }
        style={onLeadClick ? { cursor: "pointer" } : undefined}
      >
        <Avatar name={f.leadName || "?"} />
        <div className="crm-list-body">
          <div className="crm-list-name">{f.leadName}</div>
          <div className="crm-list-meta">
            {f.followedByName}
            {f.statusName ? `  ${f.statusName}` : ""}
          </div>
          {f.notes && <div className="la-reason">{f.notes}</div>}
          <div className="crm-list-followup">
            {whenLabel(f)}
            {f.daysOverdue > 0 && (
              <span className="la-overdue">  {f.daysOverdue}d overdue</span>
            )}
          </div>
        </div>
        <button
          className="crm-btn-ghost"
          onClick={(ev) => {
            ev.stopPropagation();
            setExtending(extending === f.followUpId ? null : f.followUpId);
            setExtDate("");
            setExtNotes("");
          }}
        >
          Extend
        </button>
      </div>
      {extending === f.followUpId && (
        <div className="la-fu-edit">
          <input
            type="date"
            value={extDate}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(ev) => setExtDate(ev.target.value)}
          />
          <input
            type="text"
            placeholder="Reason for new date"
            value={extNotes}
            onChange={(ev) => setExtNotes(ev.target.value)}
          />
          <button
            className="crm-btn-ghost"
            disabled={!extDate || saving}
            onClick={() => saveExtend(f)}
          >
            {saving ? "Saving" : "Save"}
          </button>
          <button className="crm-btn-ghost" onClick={() => setExtending(null)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="dw-card-loading">
        <Skeleton h={16} />
        <Skeleton h={16} />
        <Skeleton h={16} />
      </div>
    );
  }
  if (error || !data) return <EmptyState message="Couldn't load follow-ups" />;

  const tabs = [
    { name: "Missed", count: data.missedCount },
    { name: "Due Today", count: data.dueTodayCount },
    { name: "By Executive", count: data.missedByExecutive?.length ?? 0 },
  ];
  const list = tab === "Missed" ? data.missedLeads : data.dueTodayLeads;

  return (
    <>
      <style>{LEAD_ACTION_CSS}</style>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <div className="dw-scroll">
        {tab === "By Executive" ? (
          data.missedByExecutive.length === 0 ? (
            <EmptyState message="No follow-ups pending" />
          ) : (
            data.missedByExecutive.map((e) => (
              <div key={e.executiveId || "none"} className="crm-list-row">
                <Avatar name={e.executiveName || "?"} />
                <div className="crm-list-body">
                  <div className="crm-list-name">
                    {e.executiveName || "Unassigned"}
                  </div>
                  <div className="crm-list-meta">
                    {e.dueTodayCount} due today
                  </div>
                </div>
                <div className="la-remaining">
                  <b className={e.missedCount > 0 ? "la-miss-count" : ""}>
                    {e.missedCount}
                  </b>
                  <span>missed</span>
                </div>
              </div>
            ))
          )
        ) : list.length === 0 ? (
          <EmptyState
            message={
              tab === "Missed" ? "No missed follow-ups" : "Nothing due today"
            }
          />
        ) : (
          list.map(renderItem)
        )}
      </div>
    </>
  );
};

// ─── Recent Follow-up Activity (live data) ───────────────────────────────────
const RECENT_FOLLOWUPS_CSS = `
  .rf-item { display: flex; gap: 12px; padding: 10px 18px; transition: background .1s; }
  .rf-item:hover { background: #f8fafc; }
  .rf-line { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
  .rf-who { font-size: 13px; font-weight: 600; color: #1a1f36; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .rf-ago { font-size: 10.5px; color: #9ca3af; white-space: nowrap; flex-shrink: 0; }
  .rf-notes { font-size: 12px; color: #4b5563; margin-top: 2px; word-break: break-word; }
  .rf-next { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 500; color: #673AB7; margin-top: 4px; }
  .rf-next--late { color: #dc2626; }
  .rf-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; font-size: 11px; color: #9ca3af; margin-top: 3px; }
`;

const parseFollowUpDate = (date, time) => {
  if (!date) return null;
  const d = new Date(time ? `${date}T${time}` : `${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const timeAgo = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isFollowUpList = (data) =>
  Array.isArray(data) &&
  data.length > 0 &&
  data[0] &&
  typeof data[0] === "object" &&
  "nextFollowUpDate" in data[0] &&
  "notes" in data[0];

// Flatten the org tree into id -> { name, role, parentId } so follow-up rows can
// show the same people/role/manager info the Leads page has.
const buildPeopleIndex = (roots) => {
  const idx = {};
  const walk = (n, parent) => {
    if (!n) return;
    idx[n.id] = {
      name: orgFullName(n),
      role: n.accountType,
      parentId: parent?.id ?? null,
    };
    (n.children ?? []).forEach((c) => walk(c, n));
  };
  roots.forEach((r) => walk(r, null));
  return idx;
};

// Nearest ancestor with a given role (e.g. the Manager above an Executive).
const findAncestorByRole = (idx, id, role) => {
  let cur = idx[id]?.parentId;
  let guard = 0;
  while (cur && idx[cur] && guard++ < 20) {
    if (orgTypeKey(idx[cur].role) === role) return idx[cur];
    cur = idx[cur].parentId;
  }
  return null;
};

const RecentFollowUpsWidget = ({ data, onLeadClick }) => {
  const { user } = useContext(AuthContext);
  const [people, setPeople] = useState({});
  const [leadMap, setLeadMap] = useState({});

  // Newest first, using the real createdAt from the API
  const items = useMemo(
    () =>
      [...(data || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      ),
    [data],
  );

  // Same reporting structure the Org Hierarchy widget uses
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/dashboard/data/ORG_HIERARCHY?userId=${user.id}`, {
        headers: authHeader(),
      })
      .then((r) => {
        if (!cancelled) setPeople(buildPeopleIndex(normalizeOrgRoots(r.data)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Same lead source the Leads page uses, so lead name / status / assignee match
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const url =
      user.accountType === "COMPANY"
        ? `${API_URL}/api/leads/company/${user.id}`
        : `${API_URL}/api/leads/assigned/${user.id}`;
    axios
      .get(url, { headers: authHeader() })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.leads ?? (Array.isArray(r.data) ? r.data : []);
        const map = {};
        list.forEach((l) => {
          const id = l.leadId ?? l.id;
          if (id) map[id] = l;
        });
        setLeadMap(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.accountType]);

  if (items.length === 0)
    return <EmptyState message="No follow-up activity yet" />;

  return (
    <>
      <style>{RECENT_FOLLOWUPS_CSS}</style>
      <div className="dw-scroll">
        {items.map((f) => {
          const lead = leadMap[f.leadId];
          const leadName = lead?.name ?? lead?.leadName;
          const leadPhone = lead?.phone ?? lead?.contactNumber;

          // Resolve names from the org tree when the follow-up has null/empty names
          const byId = f.followedById || f.createdById;
          const person = people[byId];
          const who =
            f.followedByName?.trim() ||
            f.createdByName?.trim() ||
            person?.name ||
            "Team member";
          const role = person ? orgRoleMeta(person.role) : null;
          const manager =
            person && orgTypeKey(person.role) !== "MANAGER"
              ? findAncestorByRole(people, byId, "MANAGER")
              : null;
          const assignedTo = lead?.assignedToName;

          const next = parseFollowUpDate(f.nextFollowUpDate, f.nextFollowUpTime);
          const late = next && next.getTime() < Date.now();
          const clickable = onLeadClick && f.leadId;
          return (
            <div
              key={f.id}
              className="rf-item"
              onClick={
                clickable
                  ? () => onLeadClick({ ...(lead || {}), leadId: f.leadId })
                  : undefined
              }
              style={clickable ? { cursor: "pointer" } : undefined}
            >
              <Avatar name={leadName || who} color={role?.color} />
              <div className="crm-list-body">
                <div className="rf-line">
                  <span className="rf-who">{leadName || who}</span>
                  <span className="rf-ago">{timeAgo(f.createdAt)}</span>
                </div>
                {(leadPhone || lead?.statusName) && (
                  <div className="rf-meta">
                    {leadPhone && <span>{leadPhone}</span>}
                    {lead?.statusName && <StatusPill status={lead.statusName} />}
                  </div>
                )}
                {f.notes?.trim() && <div className="rf-notes">{f.notes}</div>}
                <div className="rf-meta">
                  <span>By {who}</span>
                  {role && (
                    <span
                      className="crm-pill"
                      style={{ background: role.color + "22", color: role.color }}
                    >
                      {role.label}
                    </span>
                  )}
                  {manager && <span>Manager: {manager.name}</span>}
                  {assignedTo && <span>Assigned to: {assignedTo}</span>}
                </div>
                {next && (
                  <div className={`rf-next ${late ? "rf-next--late" : ""}`}>
                    Next: {next.toLocaleDateString("en-IN")}
                    {f.nextFollowUpTime &&
                      " " +
                        next.toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                    {late && " · overdue"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

const DynamicWidgetBody = ({ 
  widgetKey, 
  data, 
  accent, 
  onLeadClick,
  hasMore,
  loadingMore,
  onLoadMore,
  onViewAllRecent,
  onViewAllLeads,
}) => {
  // 1. MUST BE AT THE TOP: Intercept self-fetching widgets so they bypass the generic "data" check entirely
  if (widgetKey === "APPOINTMENTS_TODAY") return <AppointmentsWidget />;
  if (widgetKey === "EXEC_WORK_SUMMARY") return <ExecWorkSummaryWidget />;
  if (widgetKey === "LEAD_TREND") return <LeadTrendWidget />;
  if (widgetKey === "ORG_HIERARCHY") return <OrgHierarchyWidget data={data} />;
  if (widgetKey === "LEAD_ACTION_REPORT") return <LeadActionReportWidget onLeadClick={onLeadClick} />;
  if (widgetKey === "EXEC_PERFORMANCE") return <ExecPerformanceWidget />;
  if (widgetKey === "FOLLOWUPS_DUE") return <FollowUpsDueWidget onLeadClick={onLeadClick} />;
  
  // 2. Standard widgets that rely on the generic backend data
  if (data === null || data === undefined)
    return <EmptyState message="No data yet" />;
    
  if (Array.isArray(data)) {
    if (data.length === 0) return <EmptyState message="Nothing to show" />;

    if (isFollowUpList(data))
      return <RecentFollowUpsWidget data={data} onLeadClick={onLeadClick} />;

    // FIX: Keep the check inside the array block, but pass the pagination props here!
    if (widgetKey === "RECENTLY_UPDATED_LEADS") {
      return (
        <RecentLeadsWidget 
          leads={data} 
          onLeadClick={onLeadClick} 
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
          onViewAll={onViewAllRecent}
        />
      );
    }

    if (widgetKey === "LEAD_SOURCE_BREAKDOWN")
      return (
        <DynamicTimeBreakdownTable
          items={data}
          nameKey="sourceName"
          accent={accent}
        />
      );
    if (widgetKey === "LEAD_PROJECT_DISTRIBUTION")
      return (
        <DynamicTimeBreakdownTable
          items={data}
          nameKey="projectName"
          accent={accent}
        />
      );
    if (widgetKey === "TODAYS_LEADS")
      return (
        <TodaysLeadsWidget
          tabs={data}
          onLeadClick={onLeadClick}
          onViewAllLeads={onViewAllLeads}
        />
      );

    if (widgetKey === "AI_CALL_RATING") {
      return (
        <div className="dw-row-list">
          {data.slice(0, 8).map((r, i) => (
            <div key={r.executiveId || i} className="crm-list-row">
              <Avatar name={r.executiveName || "?"} />
              <div className="crm-list-body">
                <div className="crm-list-name">{r.executiveName}</div>
                <div className="crm-list-meta">
                  {r.totalRatedCalls ?? 0} rated calls
                </div>
              </div>
              <StarRating rating={r.averageRating || 0} />
            </div>
          ))}
        </div>
      );
    }

    const hasValue = VALUE_KEYS.some((k) => typeof data[0]?.[k] === "number");
    return hasValue ? (
      <DynamicBarList items={data} accent={accent} />
    ) : (
      <DynamicRowList items={data} />
    );
  }

  // Handle Object data
  if (isPlainObject(data)) {
    if (widgetKey === "AI_CALL_RATING") {
      return (
        <div className="dw-rating-single">
          <StarRating rating={data.averageRating || 0} />
          <span className="dw-rating-num">
            {(data.averageRating || 0).toFixed(1)} / 5
          </span>
          <span className="dw-rating-sub">
            {data.totalRatedCalls ?? 0} rated calls
          </span>
        </div>
      );
    }
    if (widgetKey === "TASK_LIST") return <TaskListWidget taskMap={data} />;
    if (widgetKey === "TODAYS_SCHEDULE")
      return <ScheduleWidget schedule={data} />;
    if (widgetKey === "MONTHLY_ACTIVITY")
      return <MonthlyActivityWidget data={data} />;
    if (Array.isArray(data.events)) {
      return data.events.length === 0 ? (
        <EmptyState message="Nothing scheduled" />
      ) : (
        <DynamicRowList items={data.events} />
      );
    }
    return <DynamicStatGrid data={data} />;
  }

  return <EmptyState message="Unsupported widget data" />;
};

const SIZE_LABEL = { sm: "Small", md: "Medium", lg: "Large" };

const DynamicWidgetCard = ({
  widgetKey,
  label,
  category,
  size,
  userId,
  params,
  onRemove,
  onResize,
  onDragStartCard,
  onDropOnCard,
  onLeadClick,
  onLoadMore,
  innerRef,
  highlighted,
  onViewAllRecent,
  onViewAllLeads,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const pageSize = params?.limit || 50;

  const [cardRef, inView] = useInView();
  const innerRefHolder = useRef(innerRef);
  innerRefHolder.current = innerRef;
  const setCardRefs = useCallback(
    (el) => {
      cardRef.current = el;
      if (typeof innerRefHolder.current === "function") {
        innerRefHolder.current(el);
      }
    },
    [cardRef],
  );

  useEffect(() => {
    if (!userId || !inView) return undefined;
    if (SELF_FETCHING_WIDGETS.has(widgetKey)) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    setPage(0);
    setHasMore(false);
    const qs = new URLSearchParams({ userId });
    if (params?.rangeDays) qs.set("rangeDays", params.rangeDays);
    if (params?.targetUserId) qs.set("targetUserId", params.targetUserId);
    if (params?.month) qs.set("month", params.month);
    if (params?.limit) qs.set("limit", params.limit);
    if (widgetKey === "RECENTLY_UPDATED_LEADS") qs.set("page", "0");
    axios
      .get(`${API_URL}/api/dashboard/data/${widgetKey}?${qs.toString()}`, {
        headers: authHeader(),
      })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        if (widgetKey === "RECENTLY_UPDATED_LEADS" && Array.isArray(r.data)) {
          setHasMore(r.data.length === pageSize);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    widgetKey,
    userId,
    inView,
    params?.rangeDays,
    params?.targetUserId,
    params?.month,
    params?.limit,
  ]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    const qs = new URLSearchParams({ userId, page: nextPage, limit: pageSize });
    axios
      .get(`${API_URL}/api/dashboard/data/${widgetKey}?${qs.toString()}`, {
        headers: authHeader(),
      })
      .then((r) => {
        const next = Array.isArray(r.data) ? r.data : [];
        setData((prev) => (Array.isArray(prev) ? [...prev, ...next] : next));
        setPage(nextPage);
        setHasMore(next.length === pageSize);
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [widgetKey, userId, page, pageSize, hasMore, loadingMore]);

  const accent = WIDGET_COLORS[category] || "#673AB7";

  return (
    <div
      ref={setCardRefs}
      className={`dw-card dw-size-${size || "md"} ${dragOver ? "dw-drag-over" : ""} ${highlighted ? "dw-card-highlight" : ""}`}
      style={{ "--dw-accent": accent }}
      draggable={true}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragStart={(e) => {
        if (
          e.target.closest(".dw-card-tools") ||
          e.target.closest(".crm-scroll") ||
          e.target.closest(".dw-scroll") ||
          e.target.tagName === "BUTTON" ||
          e.target.tagName === "SELECT" ||
          e.target.tagName === "INPUT"
        ) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", widgetKey);
        onDragStartCard();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onDropOnCard();
      }}
      onDragEnd={() => setDragOver(false)}
    >
      <div className="dw-card-head">
        <div className="dw-card-title">
          <span className="dw-card-icon">{WIDGET_ICONS[category] || "📌"}</span>
          {label}
        </div>
        <div
          className="dw-card-tools"
          style={{
            opacity: isHovered ? 1 : 0,
            pointerEvents: isHovered ? "auto" : "none",
            transition: "opacity 0.2s",
          }}
        >
          <select
            className="dw-size-select"
            value={size || "md"}
            onChange={(e) => onResize(e.target.value)}
          >
            {Object.entries(SIZE_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <button
            className="dw-tool-btn dw-tool-remove"
            onClick={onRemove}
            title="Remove widget"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="dw-card-body">
        {loading || !inView ? (
          <div className="dw-card-loading">
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
          </div>
        ) : error ? (
          <EmptyState message="Couldn't load this widget" />
        ) : (
          <DynamicWidgetBody
            widgetKey={widgetKey}
            data={data}
            accent={accent}
            onLeadClick={onLeadClick}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            onViewAllRecent={onViewAllRecent}
            onViewAllLeads={onViewAllLeads}
          />
        )}
      </div>
    </div>
  );
};

const WidgetCustomizeDrawer = ({
  open,
  onClose,
  catalog,
  loadingCatalog,
  activeKeys,
  onToggle,
  onSave,
  saving,
}) => {
  if (!open) return null;
  const grouped = catalog.reduce((acc, w) => {
    (acc[w.category] ||= []).push(w);
    return acc;
  }, {});
  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="dw-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <h3>Customize your dashboard</h3>
          <button className="crm-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="dw-drawer-body">
          {loadingCatalog ? (
            <>
              <Skeleton h={40} />
              <Skeleton h={40} />
              <Skeleton h={40} />
            </>
          ) : catalog.length === 0 ? (
            <EmptyState message="No widgets available for your role" />
          ) : (
            Object.entries(grouped).map(([category, widgets]) => (
              <div key={category} className="dw-drawer-group">
                <div className="dw-drawer-group-title">
                  <span>{WIDGET_ICONS[category] || "📌"}</span>
                  {CATEGORY_LABELS[category] || humanizeKey(category)}
                </div>
                {widgets.map((w) => {
                  const checked = activeKeys.includes(w.widgetKey);
                  return (
                    <label key={w.widgetKey} className="dw-drawer-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggle(w)}
                      />
                      <span>{w.label}</span>
                    </label>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="crm-modal-footer">
          <button className="crm-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="crm-btn-primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save layout"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useContext(AuthContext);

  const stableUser = useMemo(
    () => ({
      id: user?.id,
      accountType: user?.accountType,
      companyId: user?.companyId,
    }),
    [user?.id, user?.accountType, user?.companyId],
  );

  const companyId = useMemo(
    () =>
      isOwnerLevel(stableUser.accountType)
        ? stableUser.id
        : stableUser.companyId,
    [stableUser],
  );

  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [loadingLeave, setLoadingLeave] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);

  const [activeMainTab, setActiveMainTab] = useState("todays");

  const [recentLeads, setRecentLeads] = useState([]);
  const [loadingRecentLeads, setLoadingRecentLeads] = useState(false);

  const [execSummary, setExecSummary] = useState([]);
  const [loadingExecSummary, setLoadingExecSummary] = useState(false);

  // --- NEW INTEGRATION START ---
  const [execRatings, setExecRatings] = useState([]);
  const [loadingExecRatings, setLoadingExecRatings] = useState(false);
  // --- NEW INTEGRATION END ---

  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentsFilter, setAppointmentsFilter] = useState("all");

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistoryLead, setSelectedHistoryLead] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [graphFilters, setGraphFilters] = useState({
    createdById: "",
    assignedToId: "",
    projectName: "",
    statusName: "",
    sourceName: "",
    fromDate: "",
    toDate: "",
  });

  const [users] = useState([]);
  const [labels] = useState([]);
  const [statusOptions] = useState([]);
  const [sourceOptions] = useState([]);
  const [permissions, setPermissions] = useState({});


  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const didInit = useRef(false);

  useEffect(() => {
    if (!stableUser.id) return;
    const saved = localStorage.getItem("userPermissions");
    if (saved) {
      setPermissions(JSON.parse(saved));
      return;
    }
    const url = isOwnerLevel(stableUser.accountType)
      ? `${API_URL}/api/user/details/${stableUser.id}`
      : `${API_URL}/api/companyuser/details/${stableUser.id}`;
    axios
      .get(url, { headers: authHeader() })
      .then((r) => {
        const p = r.data.permission?.[0] ?? {};
        setPermissions(p);
        localStorage.setItem("userPermissions", JSON.stringify(p));
      })
      .catch(console.error);
  }, [stableUser.id, stableUser.accountType]);

  const can = useCallback(
    (key) => {
      if (stableUser.accountType === ROLE.SUPER_SUPER_ADMIN) return true;
      if (isOwnerLevel(stableUser.accountType)) return true;
      return permissions[key] === true;
    },
    [stableUser.accountType, permissions],
  );

  const [dashWidgets, setDashWidgets] = useState([]);
  const [loadingDashConfig, setLoadingDashConfig] = useState(true);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [widgetCatalog, setWidgetCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [draftWidgets, setDraftWidgets] = useState([]);
  const [savingLayout, setSavingLayout] = useState(false);
  const didLoadDashConfig = useRef(false);

  // ─── ROBUST AUTO-SCROLL WHILE DRAGGING LOGIC ─────────────────────────────
  useEffect(() => {
    let animationFrameId = null;
    let scrollDirection = 0;
    let currentSpeed = 0;
    const threshold = 150;

    const scrollStep = () => {
      if (scrollDirection !== 0) {
        // Scroll window
        window.scrollBy(0, scrollDirection * currentSpeed);

        // Scroll any scrollable parent containers dynamically
        let node = document.querySelector(".crm-root");
        while (
          node &&
          node !== document.body &&
          node !== document.documentElement
        ) {
          if (node.scrollHeight > node.clientHeight) {
            const style = window.getComputedStyle(node);
            if (
              style.overflowY === "auto" ||
              style.overflowY === "scroll" ||
              style.overflowY === "overlay"
            ) {
              node.scrollBy(0, scrollDirection * currentSpeed);
            }
          }
          node = node.parentElement;
        }

        animationFrameId = requestAnimationFrame(scrollStep);
      } else {
        animationFrameId = null;
      }
    };

    const handleDragOver = (e) => {
      const y = e.clientY;
      const h = window.innerHeight;

      // Ignore invalid coordinates (often happens at drag end)
      if (y === undefined || y <= 0 || y >= h) {
        scrollDirection = 0;
        return;
      }

      let newDirection = 0;
      let speedMultiplier = 0;

      if (y < threshold) {
        newDirection = -1;
        speedMultiplier = (threshold - y) / threshold;
      } else if (y > h - threshold) {
        newDirection = 1;
        speedMultiplier = (y - (h - threshold)) / threshold;
      }

      if (newDirection !== 0) {
        scrollDirection = newDirection;
        // Calculate speed dynamically: closer to edge = faster scroll
        currentSpeed = Math.max(8, speedMultiplier * 30);
        if (!animationFrameId) {
          animationFrameId = requestAnimationFrame(scrollStep);
        }
      } else {
        scrollDirection = 0;
      }
    };

    const handleDragEnd = () => {
      scrollDirection = 0;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    // Use capture phase (true) to ensure the event is caught even if children stop propagation
    document.addEventListener("dragover", handleDragOver, true);
    document.addEventListener("dragend", handleDragEnd, true);
    document.addEventListener("drop", handleDragEnd, true);

    return () => {
      document.removeEventListener("dragover", handleDragOver, true);
      document.removeEventListener("dragend", handleDragEnd, true);
      document.removeEventListener("drop", handleDragEnd, true);
      handleDragEnd();
    };
  }, []);

  // ─── Widget search state ──────────────────────────────────────────────────
  const [widgetSearchQuery, setWidgetSearchQuery] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [highlightedWidgetKey, setHighlightedWidgetKey] = useState(null);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const widgetCardRefs = useRef({});
  const searchWrapRef = useRef(null);
  const highlightTimeoutRef = useRef(null);

  useEffect(() => {
    setActiveSearchIndex(-1);
  }, [widgetSearchQuery]);

  useEffect(() => {
    if (showSearchDropdown && activeSearchIndex >= 0) {
      const el = document.getElementById(`search-item-${activeSearchIndex}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeSearchIndex, showSearchDropdown]);

  useEffect(() => {
    if (!stableUser.id || didLoadDashConfig.current) return;
    didLoadDashConfig.current = true;
    setLoadingDashConfig(true);
    setLoadingCatalog(true);
    Promise.all([
      axios.get(`${API_URL}/api/dashboard/config/${stableUser.id}`, {
        headers: authHeader(),
      }),
      axios.get(`${API_URL}/api/dashboard/config/catalog/${stableUser.id}`, {
        headers: authHeader(),
      }),
    ])
      .then(([configR, catalogR]) => {
        setDashWidgets(
          (configR.data?.widgets ?? []).sort((a, b) => a.order - b.order),
        );
        setWidgetCatalog(catalogR.data ?? []);
      })
      .catch(console.error)
      .finally(() => {
        setLoadingDashConfig(false);
        setLoadingCatalog(false);
      });
  }, [stableUser.id]);

  const openCustomize = useCallback(() => {
    setDraftWidgets(dashWidgets);
    setCustomizeOpen(true);
  }, [dashWidgets]);

  const toggleDraftWidget = useCallback((meta) => {
    setDraftWidgets((prev) => {
      const exists = prev.some((w) => w.widgetKey === meta.widgetKey);
      if (exists) return prev.filter((w) => w.widgetKey !== meta.widgetKey);
      return [
        ...prev,
        {
          widgetKey: meta.widgetKey,
          order: prev.length,
          size: meta.defaultSize || "md",
          visible: true,
          params: {},
        },
      ];
    });
  }, []);

  const saveDashLayout = useCallback(() => {
    if (!stableUser.id) return;
    setSavingLayout(true);
    const ordered = draftWidgets.map((w, i) => ({ ...w, order: i }));
    axios
      .put(`${API_URL}/api/dashboard/config/${stableUser.id}`, ordered, {
        headers: authHeader(),
      })
      .then((r) => {
        setDashWidgets(
          (r.data?.widgets ?? ordered).sort((a, b) => a.order - b.order),
        );
        setCustomizeOpen(false);
      })
      .catch(console.error)
      .finally(() => setSavingLayout(false));
  }, [stableUser.id, draftWidgets]);

  const removeDashWidget = useCallback(
    (widgetKey) => {
      setDashWidgets((prev) => {
        const next = prev
          .filter((w) => w.widgetKey !== widgetKey)
          .map((w, i) => ({ ...w, order: i }));
        axios
          .put(`${API_URL}/api/dashboard/config/${stableUser.id}`, next, {
            headers: authHeader(),
          })
          .catch(console.error);
        return next;
      });
    },
    [stableUser.id],
  );

  const resizeDashWidget = useCallback(
    (widgetKey, size) => {
      setDashWidgets((prev) => {
        const next = prev.map((w) =>
          w.widgetKey === widgetKey ? { ...w, size } : w,
        );
        axios
          .put(`${API_URL}/api/dashboard/config/${stableUser.id}`, next, {
            headers: authHeader(),
          })
          .catch(console.error);
        return next;
      });
    },
    [stableUser.id],
  );

  const dragKeyRef = useRef(null);

  const handleDragStartWidget = useCallback((widgetKey) => {
    dragKeyRef.current = widgetKey;
  }, []);

  const handleDropWidget = useCallback(
    (targetKey) => {
      const sourceKey = dragKeyRef.current;
      dragKeyRef.current = null;
      if (!sourceKey || sourceKey === targetKey) return;

      setDashWidgets((prev) => {
        const fromIdx = prev.findIndex((w) => w.widgetKey === sourceKey);
        const toIdx = prev.findIndex((w) => w.widgetKey === targetKey);
        if (fromIdx < 0 || toIdx < 0) return prev;

        const next = [...prev];

        // Extract the widgets
        const sourceWidget = { ...next[fromIdx] };
        const targetWidget = { ...next[toIdx] };

        // Auto-resize: Swap their sizes dynamically based on their target spots!
        const tempSize = sourceWidget.size;
        sourceWidget.size = targetWidget.size;
        targetWidget.size = tempSize;

        // Swap positions
        next[fromIdx] = targetWidget;
        next[toIdx] = sourceWidget;

        const reordered = next.map((w, i) => ({ ...w, order: i }));
        axios
          .put(`${API_URL}/api/dashboard/config/${stableUser.id}`, reordered, {
            headers: authHeader(),
          })
          .catch(console.error);
        return reordered;
      });
    },
    [stableUser.id],
  );

  const [activeCategory, setActiveCategory] = useState(() => {
    try {
      return localStorage.getItem("dashboardActiveCategory");
    } catch {
      return null;
    }
  });

  const visibleWidgets = useMemo(
    () => dashWidgets.filter((w) => w.visible !== false),
    [dashWidgets],
  );

  const categoryOf = useCallback(
    (w) =>
      widgetCatalog.find((c) => c.widgetKey === w.widgetKey)?.category ||
      "reports",
    [widgetCatalog],
  );

  const categoryTabs = useMemo(() => {
    const counts = {};
    visibleWidgets.forEach((w) => {
      const c = categoryOf(w);
      counts[c] = (counts[c] || 0) + 1;
    });
    const rank = (k) => {
      const i = CATEGORY_ORDER.indexOf(k);
      return i === -1 ? CATEGORY_ORDER.length : i;
    };
    return Object.keys(counts)
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
      .map((key) => ({
        key,
        label: CATEGORY_LABELS[key] || humanizeKey(key),
        count: counts[key],
      }));
  }, [visibleWidgets, categoryOf]);

  const resolvedCategory = categoryTabs.some((t) => t.key === activeCategory)
    ? activeCategory
    : (categoryTabs[0]?.key ?? null);

  const categoryWidgets = useMemo(
    () => visibleWidgets.filter((w) => categoryOf(w) === resolvedCategory),
    [visibleWidgets, categoryOf, resolvedCategory],
  );

  const selectCategory = useCallback((key) => {
    setActiveCategory(key);
    try {
      localStorage.setItem("dashboardActiveCategory", key);
    } catch {
      return;
    }
  }, []);

  // ─── Widget search: matches + select-and-scroll handler ──────────────────
  const matchedSearchWidgets = useMemo(() => {
    const q = widgetSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return dashWidgets
      .filter((w) => w.visible !== false)
      .filter((w) => {
        const meta = widgetCatalog.find((c) => c.widgetKey === w.widgetKey);
        const label = (meta?.label || w.widgetKey || "").toLowerCase();
        return label.includes(q);
      });
  }, [widgetSearchQuery, dashWidgets, widgetCatalog]);

  const pendingScrollKey = useRef(null);

  const scrollToWidget = useCallback((widgetKey) => {
    const el = widgetCardRefs.current[widgetKey];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setHighlightedWidgetKey(widgetKey);
    highlightTimeoutRef.current = setTimeout(
      () => setHighlightedWidgetKey(null),
      2000,
    );
  }, []);

  const handleSelectSearchWidget = useCallback(
    (w) => {
      if (!w) return;
      setShowSearchDropdown(false);
      if (categoryOf(w) !== resolvedCategory) {
        pendingScrollKey.current = w.widgetKey;
        selectCategory(categoryOf(w));
        return;
      }
      scrollToWidget(w.widgetKey);
    },
    [categoryOf, resolvedCategory, selectCategory, scrollToWidget],
  );

  useEffect(() => {
    const key = pendingScrollKey.current;
    if (!key) return undefined;
    pendingScrollKey.current = null;
    const id = requestAnimationFrame(() => scrollToWidget(key));
    return () => cancelAnimationFrame(id);
  }, [resolvedCategory, scrollToWidget]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current)
        clearTimeout(highlightTimeoutRef.current);
    };
  }, []);



  useEffect(() => {
    if (!stableUser.id || isHR(stableUser.accountType)) return;
    setLoadingStats(true);
    axios
      .get(`${API_URL}/api/dashboard/data/LEAD_STATS?userId=${stableUser.id}`, {
        headers: authHeader(),
      })
      .then((r) => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoadingStats(false));
  }, [stableUser.id, stableUser.accountType]);

  useEffect(() => {
    if (!stableUser.id || !companyId || !isHR(stableUser.accountType)) return;
    setLoadingAtt(true);
    const url = `${API_URL}/api/attendance/today-report/${companyId}`;
    axios
      .get(url, { headers: authHeader() })
      .then((r) => setAttendance(r.data ?? []))
      .catch(console.error)
      .finally(() => setLoadingAtt(false));
  }, [stableUser.id, companyId, stableUser.accountType]);

  useEffect(() => {
    if (!companyId || !isHR(stableUser.accountType)) return;
    setLoadingLeave(true);
    axios
      .get(
        `${API_URL}/api/dashboard/data/PENDING_LEAVES?userId=${stableUser.id}`,
        { headers: authHeader() },
      )
      .then((r) => setPendingLeaves(r.data ?? []))
      .catch(console.error)
      .finally(() => setLoadingLeave(false));
  }, [companyId, stableUser.accountType]);

  // Lazy Fetch Recently Updated Leads API
  useEffect(() => {
    if (activeMainTab !== "recent" || recentLeads.length > 0) return;
    if (!stableUser.id || !companyId || isHR(stableUser.accountType)) return;

    setLoadingRecentLeads(true);
    const targetUserId = isOwnerLevel(stableUser.accountType)
      ? companyId
      : stableUser.id;

    axios
      .get(
        `${API_URL}/api/leads/recently-updated/${targetUserId}?page=0&size=50`,
        { headers: authHeader() },
      )
      .then((r) => {
        const result = r.data?.content || r.data || [];
        setRecentLeads(result);
      })
      .catch(console.error)
      .finally(() => setLoadingRecentLeads(false));
  }, [
    activeMainTab,
    recentLeads.length,
    stableUser.id,
    companyId,
    stableUser.accountType,
  ]);

  // Lazy Fetch Executive Work Summary API (Only when role is COMPANY)
  useEffect(() => {
    if (activeMainTab !== "exec" || execSummary.length > 0) return;
    if (!companyId || stableUser.accountType !== ROLE.COMPANY) return;

    setLoadingExecSummary(true);

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    const startDate = `${yyyy}-${mm}-01`;
    const endDate = `${yyyy}-${mm}-${dd}`;

    axios
      .get(
        `${API_URL}/api/leads/executive-work-summary/${companyId}?startDate=${startDate}&endDate=${endDate}`,
        { headers: authHeader() },
      )
      .then((r) => setExecSummary(r.data || []))
      .catch(console.error)
      .finally(() => setLoadingExecSummary(false));
  }, [activeMainTab, execSummary.length, companyId, stableUser.accountType]);

  // Lazy Fetch Appointments API
  useEffect(() => {
    if (activeMainTab !== "appointments") return;
    if (!stableUser.id || !companyId) return;

    setLoadingAppointments(true);
    let url = "";

    if (isFieldLevel(stableUser.accountType)) {
      url = `${API_URL}/api/appointments/executive/${stableUser.id}?pageNumber=0&pageSize=10`;
    } else {
      url =
        appointmentsFilter === "today"
          ? `${API_URL}/api/appointments/company/${companyId}/today?pageNumber=0&pageSize=50`
          : `${API_URL}/api/appointments/company/${companyId}?pageNumber=0&pageSize=10`;
    }

    axios
      .get(url, { headers: authHeader() })
      .then((r) => {
        const data =
          r.data?.appointments ||
          r.data?.content ||
          (Array.isArray(r.data) ? r.data : []);
        setAppointments(data);
      })
      .catch(console.error)
      .finally(() => setLoadingAppointments(false));
  }, [
    activeMainTab,
    appointmentsFilter,
    stableUser.id,
    companyId,
    stableUser.accountType,
  ]);

  // --- NEW INTEGRATION START ---
  // Lazy Fetch Executive Ratings API based on companyId
  useEffect(() => {
    if (activeMainTab !== "ratings" || execRatings.length > 0) return;
    if (!companyId) return;

    setLoadingExecRatings(true);
    axios
      .get(
        `${API_URL}/api/transcription/company/${companyId}/executive-ratings`,
        { headers: authHeader() },
      )
      .then((r) => setExecRatings(r.data ?? []))
      .catch(console.error)
      .finally(() => setLoadingExecRatings(false));
  }, [activeMainTab, companyId, execRatings.length]);
  // --- NEW INTEGRATION END ---

  // ─── Redirect to the dedicated Recently Updated Leads page ────────────────
  // Lead objects from Today's Leads / Followups can have a slightly different
  // shape than "recently updated" leads, so normalise the key fields first.
  const normalizeLead = (lead) => ({
    ...lead,
    leadId: lead.leadId ?? lead.id,
    name: lead.name ?? lead.leadName,
    phone: lead.phone ?? lead.contactNumber,
  });

  const handleRecentLeadClick = useCallback((lead) => {
    navigate("/dashboard/recentleads", {
      state: { lead: normalizeLead(lead), mode: "single" },
    });
  }, [navigate]);

  // Today's Leads / Followups widgets -> "View Full" opens the whole active tab
  const handleViewAllLeads = useCallback((tabName, leads = []) => {
    const title =
      tabName === "Followups"
        ? "Today's Followups"
        : `Today's Leads${tabName ? ` - ${tabName}` : ""}`;
    navigate("/dashboard/recentleads", {
      state: { mode: "list", title, leads: leads.map(normalizeLead) },
    });
  }, [navigate]);

  const leadListBusy = useRef(false);

  const openLeadList = useCallback(
    async (kind) => {
      if (!stableUser.id || leadListBusy.current) return;
      leadListBusy.current = true;
      try {
        let leads = [];
        if (kind === "Followups") {
          const r = await axios.get(
            `${API_URL}/api/leads/today-followups/${companyId}/${stableUser.id}`,
            { headers: authHeader() },
          );
          leads = r.data ?? [];
        } else {
          const r = await axios.get(
            `${API_URL}/api/leads/groupedByStatus/${stableUser.id}`,
            { headers: authHeader() },
          );
          const group = (r.data ?? []).find((g) => g.status === kind);
          leads = group?.leads ?? [];
        }
        handleViewAllLeads(kind, leads);
      } catch (e) {
        console.error("Lead list fetch error:", e);
      } finally {
        leadListBusy.current = false;
      }
    },
    [stableUser.id, companyId, handleViewAllLeads],
  );

  const handleViewAllRecent = useCallback(() => {
    navigate("/dashboard/recentleads", { state: { mode: "all" } });
  }, [navigate]);


  const fetchGraph = useCallback(
    async (params) => {
      if (!companyId) return;
      setLoadingGraph(true);
      try {
        const r = await axios.post(
          `${API_URL}/api/leads/graphDataSearch`,
          { ...params, companyId },
          { headers: authHeader() },
        );
        const data = r.data;
        if (Array.isArray(data)) {
          setSources(
            data.map((d) => ({
              sourceName: d.label || "Unknown",
              count: d.count || 0,
            })),
          );
        } else {
          setSources([{ sourceName: "All Leads", count: data }]);
        }
      } catch (e) {
        console.error("Graph fetch error:", e);
      } finally {
        setLoadingGraph(false);
      }
    },
    [companyId],
  );

  const statCards = useMemo(() => {
    if (isHR(stableUser.accountType)) {
      const total = attendance.length;
      const present = attendance.filter((a) => a.status === "PRESENT").length;
      return [
        {
          label: "Present today",
          value: present,
          icon: "✓",
          accent: "#16a34a",
          sub: `of ${total} total`,
        },
        {
          label: "Absent today",
          value: attendance.filter((a) => a.status === "ABSENT").length,
          icon: "✗",
          accent: "#dc2626",
        },
        {
          label: "On leave",
          value: attendance.filter((a) => a.status === "ON_LEAVE").length,
          icon: "🌴",
          accent: "#7c3aed",
        },
        {
          label: "Pending requests",
          value: pendingLeaves.length,
          icon: "📋",
          accent: "#d97706",
        },
      ];
    }
    if (!stats) return [];

    return [
      {
        label: "Total leads",
        value: stats.totalLeads,
        icon: "📊",
        accent: "#673AB7",
      },
      {
        label: "New today",
        value: stats.newToday,
        icon: "🌟",
        accent: "#10b981",
        sub: "vs yesterday",
        onClick: () => openLeadList("New")
      },
      {
        label: "This month",
        value: stats.thisMonth,
        icon: "📅",
        accent: "#f59e0b",
      },
      {
        label: "Conversations",
        value: stats.conversions,
        icon: "🎯",
        accent: "#ef4444",
        sub: stats.totalLeads
          ? `${Math.round((stats.conversions / stats.totalLeads) * 100)}% rate`
          : "",
        onClick: () => navigate("/customer")
      },
      {
        label: "Follow-ups today",
        value: stats.followUpsDueToday,
        icon: "⏰",
        accent: "#8b5cf6",
        onClick: () => openLeadList("Followups")
      },
    ];
  }, [stats, stableUser.accountType, attendance, pendingLeaves, navigate, openLeadList]);

  if (loading) {
    return (
      <div className="crm-loading-screen">
        <div className="crm-spinner crm-spinner--lg" />
        <p>Loading your workspace…</p>
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="crm-loading-screen">
        <p>Please log in to continue.</p>
      </div>
    );
  }

  const role = stableUser.accountType;

  return (
    <>
      <style>{CRM_CSS}</style>
      <div className="crm-root">
        {/* Top bar */}
        <div className="crm-topbar">
          <div>
            <h1 className="crm-page-title">
              {isHR(role)
                ? "HR Dashboard"
                : isOwnerLevel(role)
                  ? "Company Overview"
                  : isMidLevel(role)
                    ? "Team Dashboard"
                    : "My Dashboard"}
            </h1>
            <p className="crm-page-sub">
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              <span className="crm-role-badge">{role?.replace("_", " ")}</span>
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            {/* 1. Customize Button */}
            <button
              className="crm-btn-ghost dw-customize-btn"
              onClick={openCustomize}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 6 }}
              >
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              Customize
            </button>

            {/* 2. Full Screen Button */}
            <button
              className="crm-btn-ghost"
              onClick={toggleFullScreen}
              title="Toggle Fullscreen"
            >
              {isFullscreen ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: 6 }}
                >
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              ) : (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: 6 }}
                >
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              )}
              {isFullscreen ? "Exit Full" : "Full Screen"}
            </button>

            {/* 3. Sales God Button */}
            {can("leads") && (
              <button
                className="crm-btn-primary"
                style={{ backgroundColor: "#0C4550", color: "#fff" }}
                onClick={() => navigate("/dashboard/sales-god")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ marginRight: 6 }}
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                Sales God
              </button>
            )}

            {can("leads") && (
              <button
                className="crm-btn-primary"
                style={{ backgroundColor: "#673AB7", color: "#fff" }}
                onClick={() => navigate("/dashboard/lead-funnel")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: 6 }}
                >
                  <path d="M3 4h18l-7 8.5V19l-4 2v-8.5L3 4z" />
                </svg>
                Lead Funnel
              </button>
            )}

            {/* 4. Refresh Button */}
            <button
              className="crm-btn-primary"
              onClick={() => {
                didInit.current = false;
                window.location.reload();
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Stat cards */}
        {statCards.length > 0 && (
          <div className="crm-stats-row">
            {loadingStats || (isHR(role) && loadingAtt)
              ? Array(4)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="crm-stat-card">
                      <Skeleton h={60} />
                    </div>
                  ))
              : statCards.map((s) => <StatBadge key={s.label} {...s} />)}
          </div>
        )}

        <div className="dw-section">
          <div className="crm-section-header">
            <div>
              <h2 className="crm-section-title">Your Widgets</h2>
              <p className="crm-section-sub">
                Drag any widget to reorder. Size adjusts automatically.
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {/* Widget Search Bar */}
              <div className="dw-search-wrap" ref={searchWrapRef}>
                <svg
                  className="dw-search-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  className="dw-search-input"
                  placeholder="Search widgets…"
                  value={widgetSearchQuery}
                  onChange={(e) => {
                    setWidgetSearchQuery(e.target.value);
                    setShowSearchDropdown(true);
                  }}
                  onFocus={() => {
                    if (widgetSearchQuery.trim()) setShowSearchDropdown(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      if (matchedSearchWidgets.length === 0) return;
                      setShowSearchDropdown(true);
                      setActiveSearchIndex((prev) =>
                        prev < matchedSearchWidgets.length - 1 ? prev + 1 : 0,
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      if (matchedSearchWidgets.length === 0) return;
                      setShowSearchDropdown(true);
                      setActiveSearchIndex((prev) =>
                        prev > 0 ? prev - 1 : matchedSearchWidgets.length - 1,
                      );
                    } else if (e.key === "Enter") {
                      if (matchedSearchWidgets.length > 0) {
                        const idx =
                          activeSearchIndex >= 0 &&
                          activeSearchIndex < matchedSearchWidgets.length
                            ? activeSearchIndex
                            : 0;
                        handleSelectSearchWidget(matchedSearchWidgets[idx]);
                      }
                    } else if (e.key === "Escape") {
                      setShowSearchDropdown(false);
                    }
                  }}
                />
                {showSearchDropdown && widgetSearchQuery.trim() && (
                  <div className="dw-search-dropdown">
                    {matchedSearchWidgets.length === 0 ? (
                      <div className="dw-search-dropdown-empty">
                        No matching widgets
                      </div>
                    ) : (
                      matchedSearchWidgets.map((w, idx) => {
                        const meta = widgetCatalog.find(
                          (c) => c.widgetKey === w.widgetKey,
                        );
                        return (
                          <div
                            key={w.widgetKey}
                            id={`search-item-${idx}`}
                            className={`dw-search-dropdown-item ${idx === activeSearchIndex ? "dw-search-dropdown-item--active" : ""}`}
                            onMouseEnter={() => setActiveSearchIndex(idx)}
                            onClick={() => handleSelectSearchWidget(w)}
                          >
                            <span className="dw-search-item-icon">
                              {WIDGET_ICONS[meta?.category] || "📌"}
                            </span>
                            {meta?.label || w.widgetKey}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <button className="crm-btn-ghost" onClick={openCustomize}>
                Add widgets
              </button>
            </div>
          </div>
          {loadingDashConfig ? (
            <div className="dw-grid">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="dw-card dw-size-md">
                    <Skeleton h={90} />
                  </div>
                ))}
            </div>
          ) : dashWidgets.length === 0 ? (
            <div className="dw-empty-panel">
              <EmptyState message="Your dashboard is empty — add widgets to get started" />
              <button className="crm-btn-primary" onClick={openCustomize}>
                Add widgets
              </button>
            </div>
          ) : (
            <>
              <div className="crm-tabs dw-cat-tabs">
                {categoryTabs.map((t) => (
                  <button
                    key={t.key}
                    className={`crm-tab ${resolvedCategory === t.key ? "crm-tab--active" : ""}`}
                    onClick={() => selectCategory(t.key)}
                  >
                    <span>{WIDGET_ICONS[t.key] || "📌"}</span>
                    {t.label}
                    <span className="crm-tab-badge">{t.count}</span>
                  </button>
                ))}
              </div>
              <div className="dw-grid">
                {categoryWidgets.map((w) => {
                  const meta = widgetCatalog.find(
                    (c) => c.widgetKey === w.widgetKey,
                  );
                  return (
                    <DynamicWidgetCard
                      key={w.widgetKey}
                      widgetKey={w.widgetKey}
                      label={meta?.label || w.widgetKey}
                      category={meta?.category || "reports"}
                      size={w.size}
                      userId={stableUser.id}
                      params={w.params}
                      onRemove={() => removeDashWidget(w.widgetKey)}
                      onResize={(sz) => resizeDashWidget(w.widgetKey, sz)}
                      onDragStartCard={() => handleDragStartWidget(w.widgetKey)}
                      onDropOnCard={() => handleDropWidget(w.widgetKey)}
                      onLeadClick={handleRecentLeadClick}
                      onViewAllRecent={handleViewAllRecent}
                      onViewAllLeads={handleViewAllLeads}
                      innerRef={(el) => {
                        widgetCardRefs.current[w.widgetKey] = el;
                      }}
                      highlighted={highlightedWidgetKey === w.widgetKey}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Filter modal */}
        <FilterModal
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          filters={graphFilters}
          onChange={(e) =>
            setGraphFilters((p) => ({ ...p, [e.target.name]: e.target.value }))
          }
          onApply={() => {
            fetchGraph(graphFilters);
            setShowFilterModal(false);
          }}
          onReset={() => {
            const reset = {
              createdById: stableUser.id,
              assignedToId: "",
              projectName: "",
              statusName: "",
              sourceName: "",
              fromDate: "",
              toDate: "",
            };
            setGraphFilters(reset);
            fetchGraph(reset);
          }}
          users={users}
          labels={labels}
          statusOptions={statusOptions}
          sourceOptions={sourceOptions}
        />

        <WidgetCustomizeDrawer
          open={customizeOpen}
          onClose={() => setCustomizeOpen(false)}
          catalog={widgetCatalog}
          loadingCatalog={loadingCatalog}
          activeKeys={draftWidgets.map((w) => w.widgetKey)}
          onToggle={toggleDraftWidget}
          onSave={saveDashLayout}
          saving={savingLayout}
        />

        {/* Lead History Modal */}
        <LeadHistoryModal
          open={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          historyData={historyData}
          loading={loadingHistory}
          lead={selectedHistoryLead}
        />
      </div>
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
//  CSS
// ──────────────────────────────────────────────────────────────────────────────
const CRM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
/* ───────────────────────────────────────────────────────────────
   SQUARE CORNERS EVERYWHERE
   Paste at the VERY END of CRM_CSS (after the compact theme block).
   !important is used so it also beats inline border-radius styles
   (avatars, skeletons, progress bars, pills, etc.).
   ─────────────────────────────────────────────────────────────── */

.crm-root,
.crm-root *,
.crm-root *::before,
.crm-root *::after {
  border-radius: 0 !important;
}

/* Keep the loading spinner and tiny status dots round so they still work/read correctly.
   Delete this block if you want those square too. */
.crm-root .crm-spinner,
.crm-root .crm-task-dot,
.crm-root .org-status,
.crm-root .org-chip-dot,
.crm-root .dw-time-dot {
  border-radius: 50% !important;
}

/* Square form controls, dropdowns and modals (in case any render outside .crm-root) */
.crm-modal-overlay,
.crm-modal-overlay *,
.dw-drawer,
.crm-modal,
.crm-month-dropdown,
.dw-search-dropdown {
  border-radius: 0 !important;
}
  .crm-root { font-family: 'Plus Jakarta Sans', sans-serif; background: radial-gradient(1200px 400px at 10% -10%, #f3ecff 0%, #EEF2FC 45%, #EEF2FC 100%); min-height: 100vh; padding: 0 0 40px; color: #1a1f36; }

  .crm-topbar { background: rgba(255,255,255,.85); backdrop-filter: blur(10px); border-bottom: 1px solid #e5e9f0; padding: 18px 28px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 10; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
  .crm-page-title { font-size: 20px; font-weight: 700; color: #673AB7; letter-spacing: -0.3px; }
  .crm-page-sub { font-size: 12px; color: #6b7280; margin-top: 2px; display: flex; align-items: center; gap: 10px; }
  .crm-role-badge { background: #f3e8ff; color: #673AB7; font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 20px; text-transform: uppercase; letter-spacing: .04em; }

  .crm-stats-row { display: flex; gap: 14px; padding: 20px 28px 0; flex-wrap: wrap; }
  .crm-stat-card { flex: 1; min-width: 160px; background: #fff; border: 1px solid #eef1f6; border-radius: 16px; padding: 16px 18px; display: flex; align-items: flex-start; gap: 14px; position: relative; overflow: hidden; box-shadow: 0 1px 2px rgba(16,24,40,.04); transition: box-shadow .2s, transform .2s; }
  .crm-stat-card:hover { box-shadow: 0 4px 10px rgba(16,24,40,.05), 0 16px 28px -14px rgba(103,58,183,.22); transform: translateY(-2px); }
  .crm-stat-bar { position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--accent,#673AB7), color-mix(in srgb, var(--accent,#673AB7) 40%, white)); opacity: .85; }
  .crm-stat-icon { font-size: 22px; width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(145deg, color-mix(in srgb,var(--accent,#673AB7) 18%,white), color-mix(in srgb,var(--accent,#673AB7) 8%,transparent)); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .crm-stat-body { flex: 1; min-width: 0; }
  .crm-stat-val { font-size: 26px; font-weight: 700; color: #1a1f36; line-height: 1; font-variant-numeric: tabular-nums; }
  .crm-stat-label { font-size: 12px; color: #6b7280; margin-top: 4px; font-weight: 500; }
  .crm-stat-sub { font-size: 11px; color: var(--accent,#673AB7); margin-top: 3px; font-weight: 500; }

  .crm-full-width-section { padding: 20px 28px 0; }
  .crm-unified-header { display: flex; gap: 4px; padding: 12px 18px 0; border-bottom: 1px solid #f1f5f9; overflow-x: auto; background: #fff; }
  .crm-unified-tab { padding: 10px 16px; font-size: 13px; font-weight: 600; color: #6b7280; background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; white-space: nowrap; transition: color 0.2s, border-color 0.2s; font-family: inherit; }
  .crm-unified-tab:hover { color: #1a1f36; }
  .crm-unified-tab.active { color: #0C4550; border-bottom-color: #0C4550; }

  .crm-appt-card { background: #f8fafc; border: 1px solid #e5e9f0; border-radius: 12px; padding: 16px; margin-bottom: 12px; transition: box-shadow 0.2s; }
  .crm-appt-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
  .crm-appt-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 10px; }
  .crm-appt-dept { font-size: 14px; color: #1a1f36; }
  .crm-appt-label { font-weight: 600; color: #0C4550; }
  .crm-appt-row { font-size: 13px; color: #4b5563; margin-bottom: 10px; }
  .crm-appt-row:last-child { margin-bottom: 0; }
  .crm-appt-sub-text { color: #9ca3af; }
  .crm-appt-datetime { display: flex; gap: 20px; font-size: 13px; color: #4b5563; margin-bottom: 10px; flex-wrap: wrap; }
  .crm-appt-dt-item { display: flex; align-items: center; }

  /* ─ Responsive Grid Logic for Dashboard Body ─ */
  .crm-main-grid { display: grid; grid-template-columns: 1fr 380px; gap: 20px; padding: 20px 28px 0; align-items: start; }
  .crm-main-grid--no-main { display: block; }
  .crm-main-grid--no-main .crm-col--side { display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 20px; width: 100%; }
  .crm-main-grid--no-side { grid-template-columns: 1fr; }
  @media (max-width: 1100px) { .crm-main-grid { grid-template-columns: 1fr; } }
  .crm-col { display: flex; flex-direction: column; gap: 18px; }
  
  /* ─ Adjusting Cards to fix artificial blank spaces ─ */
  .crm-widget { height: fit-content; display: flex; flex-direction: column; width: 100%; }
  .crm-activity-section { padding: 18px 28px 0; min-height: 340px; }

  .crm-card { background: #fff; border: 1px solid #eef1f6; border-radius: 18px; overflow: hidden; display: flex; flex-direction: column; height: fit-content; max-height: 100%; box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.08); transition: box-shadow .2s; width: 100%; }
  .crm-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,.07); }

  .crm-section-header { padding: 16px 18px 12px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
  .crm-section-title { font-size: 14px; font-weight: 700; color: #673AB7; letter-spacing: -0.1px; }
  .crm-section-sub { font-size: 11px; color: #9ca3af; margin-top: 2px; }

  .crm-card-body { padding: 6px 0; flex: 1; min-height: 0; }
  .crm-scroll { overflow-y: auto; max-height: 280px; }
  
  .crm-scroll::-webkit-scrollbar { width: 6px; }
  .crm-scroll::-webkit-scrollbar-track { background: transparent; }
  .crm-scroll::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 4px; transition: background .15s; }
  .crm-scroll::-webkit-scrollbar-thumb:hover { background: #374151; }

  .crm-tabs { display: flex; border-bottom: 1px solid #f1f5f9; padding: 0 14px; gap: 2px; overflow-x: auto; flex-shrink: 0; }
  .crm-tab { padding: 10px 12px; font-size: 12px; font-weight: 500; color: #6b7280; background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; white-space: nowrap; display: flex; align-items: center; gap: 6px; transition: color .15s, border-color .15s; }
  .crm-tab:hover { color: #1a1f36; }
  .crm-tab--active { color: #673AB7; border-bottom-color: #673AB7; font-weight: 600; }
  .crm-tab-badge { background: #f1f5f9; color: #6b7280; font-size: 10px; padding: 1px 6px; border-radius: 10px; font-weight: 600; }
  .crm-tab--active .crm-tab-badge { background: #f3e8ff; color: #673AB7; }

  .crm-list-row { display: flex; align-items: center; gap: 12px; padding: 10px 18px; transition: background .1s; }
  .crm-list-row:hover { background: #f8fafc; }
  .crm-list-body { flex: 1; min-width: 0; }
  .crm-list-name { font-size: 13px; font-weight: 600; color: #1a1f36; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .crm-list-meta { font-size: 11px; color: #9ca3af; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .crm-list-followup { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #673AB7; margin-top: 3px; font-weight: 500; }

  .crm-avatar { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; letter-spacing: .05em; }
  .crm-task-dot { width: 8px; height: 8px; border-radius: 50%; background: #673AB7; flex-shrink: 0; }
  .crm-pill { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 20px; white-space: nowrap; flex-shrink: 0; }
  .crm-pill-active { background: #0C4550; color: #fff; border: 1px solid #0C4550; }

  .crm-mini-stats { display: grid; grid-template-columns: repeat(4,1fr); gap: 1px; background: #f1f5f9; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; user-select: none; }
  .crm-mini-stat { background: #fff; padding: 10px; text-align: center; border-right: 1px solid #f1f5f9; }
  .crm-mini-stat:last-child { border-right: none; }
  .crm-mini-val { display: block; font-size: 20px; font-weight: 700; color: var(--c,#1a1f36); line-height: 1; }
  .crm-mini-label { display: block; font-size: 10px; color: #9ca3af; margin-top: 3px; font-weight: 500; }

  .crm-team-row { display: flex; align-items: center; gap: 12px; padding: 10px 18px; transition: background .1s; }
  .crm-team-row:hover { background: #f8fafc; }
  .crm-team-bar-wrap { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
  .crm-team-bar-track { flex: 1; height: 5px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
  .crm-team-bar-fill { height: 100%; border-radius: 3px; transition: width .6s ease; }
  .crm-team-bar-label { font-size: 10px; color: #9ca3af; width: 28px; text-align: right; flex-shrink: 0; font-weight: 600; }
  .crm-team-nums { text-align: right; flex-shrink: 0; }
  .crm-team-num-big { display: block; font-size: 15px; font-weight: 700; color: #1a1f36; line-height: 1; }
  .crm-team-num-small { display: block; font-size: 10px; color: #9ca3af; margin-top: 2px; font-weight: 500; }

  .crm-donut-center { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-64%); text-align: center; pointer-events: none; }
  .crm-donut-total { font-size: 26px; font-weight: 700; color: #1a1f36; line-height: 1; }
  .crm-donut-label { font-size: 11px; color: #9ca3af; margin-top: 2px; font-weight: 500; }

  .crm-activity-header { padding: 16px 18px 12px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
  .crm-activity-controls { display: flex; gap: 8px; flex-wrap: wrap; }
  .crm-chart-wrap { padding: 14px 18px; height: 220px; position: relative; flex: 1; }

  .crm-select { background: #f8fafc; border: 1px solid #e5e9f0; border-radius: 8px; padding: 7px 12px; font-size: 12px; color: #1a1f36; font-family: inherit; cursor: pointer; outline: none; transition: border-color .15s; appearance: none; min-width: 130px; }
  .crm-select:hover, .crm-select:focus { border-color: #673AB7; }

  .crm-month-picker { position: relative; }
  .crm-month-trigger { display: flex; align-items: center; justify-content: space-between; cursor: pointer; user-select: none; }
  .crm-month-dropdown { position: absolute; right: 0; top: calc(100% + 6px); background: #fff; border: 1px solid #e5e9f0; border-radius: 12px; box-shadow: 0 8px 30px rgba(0,0,0,.12); width: 220px; z-index: 200; overflow: hidden; }
  .crm-month-nav { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-bottom: 1px solid #f1f5f9; font-size: 13px; font-weight: 600; color: #1a1f36; }
  .crm-month-nav button { background: none; border: none; cursor: pointer; font-size: 16px; color: #6b7280; padding: 0 4px; line-height: 1; transition: color .1s; }
  .crm-month-nav button:hover { color: #673AB7; }
  .crm-month-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 4px; padding: 10px; }
  .crm-month-btn { padding: 7px 4px; font-size: 12px; border-radius: 7px; border: none; background: none; cursor: pointer; color: #4b5563; font-weight: 500; transition: background .1s, color .1s; font-family: inherit; }
  .crm-month-btn:hover { background: #f1f5f9; }
  .crm-month-btn--curr { background: #f3e8ff; color: #673AB7; }
  .crm-month-btn--sel { background: #673AB7 !important; color: #fff !important; }

  .crm-btn-primary { background: #673AB7; color: #fff; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; transition: background .15s, transform .1s; }
  .crm-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
  .crm-btn-ghost { background: #f8fafc; color: #4b5563; border: 1px solid #e5e9f0; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; transition: background .1s, border-color .1s; }
  .crm-btn-ghost:hover { background: #f1f5f9; border-color: #d1d5db; }

  .crm-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; backdrop-filter: blur(3px); }
  .crm-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 480px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.2); }
  
  .crm-modal-header { padding: 18px 20px 14px; background-color: #673AB7; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; border-radius: 16px 16px 0 0; }
  .crm-modal-header h3 { font-size: 16px; font-weight: 700; color: #fff; margin: 0; }
  
  .crm-modal-close { 
    background: transparent !important; 
    color: #fff !important; 
    border: none; 
    font-size: 16px; 
    cursor: pointer; 
    width: 28px; 
    height: 28px; 
    display: flex; 
    align-items: center; 
    justify-content: center; 
  }
  .crm-modal-close:hover { 
    background: transparent !important; 
    color: #fff !important; 
  }

  .crm-modal-body { padding: 18px 20px; display: flex; flex-direction: column; gap: 14px; overflow-y: auto; }
  .crm-modal-footer { padding: 14px 20px; border-top: 1px solid #f1f5f9; display: flex; gap: 8px; justify-content: flex-end; }
  .crm-form-row { display: flex; flex-direction: column; gap: 5px; }
  .crm-form-label { font-size: 12px; font-weight: 600; color: #4b5563; }
  .crm-form-select { background: #f8fafc; border: 1px solid #e5e9f0; border-radius: 8px; padding: 8px 12px; font-size: 13px; color: #1a1f36; font-family: inherit; outline: none; width: 100%; transition: border-color .15s; }
  .crm-form-select:focus { border-color: #673AB7; background: #fff; }
  .crm-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  .crm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px 20px; color: #d1d5db; gap: 10px; text-align: center; }
  .crm-empty p { font-size: 12px; color: #9ca3af; font-weight: 500; margin: 0; }

  .crm-skeleton { background: linear-gradient(90deg,#f1f5f9 25%,#e9eef5 50%,#f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; margin: 8px 18px; display: block; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  .crm-spinner { width: 24px; height: 24px; border: 2.5px solid #e5e9f0; border-top-color: #673AB7; border-radius: 50%; animation: spin .7s linear infinite; }
  .crm-spinner--lg { width: 40px; height: 40px; border-width: 3.5px; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .crm-center { display: flex; align-items: center; justify-content: center; height: 100%; }
  .crm-loading-screen { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; background: #ffffff; font-size: 14px; color: #6b7280; }

  .sg2-history-modal-container { max-width: 560px; }
  .sg2-history-lead-strip { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #f1f5f9; background: #f8fafc; }
  .sg2-history-lead-meta { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .sg2-history-lead-name { font-size: 14px; font-weight: 700; color: #1a1f36; }
  .sg2-history-lead-sub { display: flex; flex-wrap: wrap; gap: 10px; font-size: 12px; color: #6b7280; font-weight: 500; }
  .sg2-history-body-wrap { max-height: 60vh; overflow-y: auto; gap: 12px; }
  .sg2-history-empty { text-align: center; color: #9ca3af; font-size: 13px; padding: 24px 0; margin: 0; }
  .sg2-history-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 10px; }
  .sg2-history-card-top { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
  .sg2-history-avatar { width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0; background: #e0e8f5; color: #0C4550; font-weight: 700; font-size: 13px; display: flex; align-items: center; justify-content: center; }
  .sg2-history-changer { font-size: 13px; font-weight: 700; color: #1a1f36; }
  .sg2-history-time { font-size: 11px; color: #9ca3af; font-weight: 500; }
  .sg2-history-card-body { border-left: 2px solid #673AB7; padding-left: 12px; margin-left: 3px; }
  .sg2-history-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .sg2-history-row:last-child { margin-bottom: 0; }
  .sg2-history-dot { width: 6px; height: 6px; border-radius: 50%; background: #673AB7; flex-shrink: 0; }
  .sg2-history-dot--green { background: #16a34a; }
  .sg2-history-created-text { font-size: 12.5px; color: #374151; font-weight: 500; }
  .sg2-history-change { margin-bottom: 10px; }
  .sg2-history-change:last-child { margin-bottom: 0; }
  .sg2-history-change-field { font-size: 11px; font-weight: 700; color: #4b5563; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 4px; }
  .sg2-history-change-values { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .sg2-history-from { font-size: 11.5px; padding: 2px 8px; border-radius: 6px; background: #fef2f2; color: #dc2626; font-weight: 600; }
  .sg2-history-to { font-size: 11.5px; padding: 2px 8px; border-radius: 6px; background: #f0fdf4; color: #16a34a; font-weight: 600; }
  .sg2-history-arrow { color: #9ca3af; font-size: 12px; }

  .dw-customize-btn { position: relative; }

  .dw-section { padding: 6px 28px 4px; }
  .dw-empty-panel { background: #fff; border: 1.5px dashed #d8deea; border-radius: 18px; padding: 30px 20px; display: flex; flex-direction: column; align-items: center; gap: 14px; }

  /* ─ Widget Search Bar ─ */
  .dw-search-wrap { position: relative; display: flex; align-items: center; }
  .dw-search-icon { position: absolute; left: 10px; color: #673AB7; pointer-events: none; }
  .dw-search-input { padding: 7px 12px 7px 30px; border: 1px solid #e5e9f0; border-radius: 8px; font-size: 12px; font-family: inherit; color: #1a1f36; background: #f8fafc; outline: none; width: 220px; transition: border-color .15s, box-shadow .15s, background .15s; }
  .dw-search-input::placeholder { color: #9ca3af; }
  .dw-search-input:focus { border-color: #673AB7; box-shadow: 0 0 0 3px rgba(103,58,183,.14); background: #fff; }
  .dw-search-dropdown { position: absolute; top: calc(100% + 6px); left: 0; right: 0; background: #fff; border: 1px solid #e5e9f0; border-radius: 10px; box-shadow: 0 8px 30px rgba(0,0,0,.12); max-height: 260px; overflow-y: auto; z-index: 300; }
  .dw-search-dropdown-item { display: flex; align-items: center; gap: 8px; padding: 9px 12px; font-size: 12.5px; font-weight: 500; color: #1a1f36; cursor: pointer; transition: background .1s, color .1s; }
  
  .dw-search-dropdown-item:hover, .dw-search-dropdown-item--active { background: #f3e8ff; color: #673AB7; }
  
  .dw-search-item-icon { font-size: 13px; }
  .dw-search-dropdown-empty { padding: 12px; font-size: 12px; color: #9ca3af; text-align: center; }

  .dw-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 16px; }
  .dw-cat-tabs { padding: 0; margin-bottom: 14px; }
  
  /* Make all cards draggable everywhere with grab cursor */
  .dw-card { cursor: grab; grid-column: span 4; background: #fff; border-radius: 18px; box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.10); border: 1px solid #eef1f6; overflow: hidden; display: flex; flex-direction: column; transition: box-shadow .2s ease, transform .2s ease; }
  .dw-card:active { cursor: grabbing; }
  .dw-card:hover { box-shadow: 0 4px 10px rgba(16,24,40,.06), 0 16px 32px -14px rgba(16,24,40,.16); transform: translateY(-2px); }
  
  /* Reset cursor for inner scrollable areas so users know they can interact */
  .dw-card-body { cursor: auto; padding: 14px 16px 16px; flex: 1; }
  
  .dw-drag-over { outline: 2px dashed #673AB7; outline-offset: -2px; background: #faf5ff; }
  .dw-size-sm { grid-column: span 3; }
  .dw-size-md { grid-column: span 4; }
  .dw-size-lg { grid-column: span 6; }
  @media (max-width: 1200px) { .dw-size-sm, .dw-size-md, .dw-size-lg { grid-column: span 6; } }
  @media (max-width: 720px) { .dw-grid { grid-template-columns: 1fr; } .dw-size-sm, .dw-size-md, .dw-size-lg { grid-column: span 1; } }

  /* ─ Search Highlight Effect ─ */
  .dw-card-highlight { animation: dwHighlightZoom 2s ease-in-out forwards; z-index: 10; position: relative; }
  @keyframes dwHighlightZoom {
    0%   { transform: scale(1); box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.10); }
    15%  { transform: scale(1.05); box-shadow: -4px -4px 15px #e9d5ff, 4px 4px 15px #a78bfa, 0 0 0 3px #d8b4fe; }
    85%  { transform: scale(1.05); box-shadow: -4px -4px 15px #e9d5ff, 4px 4px 15px #a78bfa, 0 0 0 3px #d8b4fe; }
    100% { transform: scale(1); box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.10); }
  }

  .dw-card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 16px; border-bottom: 1px solid #f1f5f9; background: linear-gradient(180deg, rgba(103,58,183,.03), transparent); }
  .dw-card-title { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 700; color: #1a1f36; }
  .dw-card-icon { font-size: 15px; line-height: 1; }
  .dw-card-tools { display: flex; align-items: center; gap: 4px; }
  .dw-tool-btn { width: 24px; height: 24px; border-radius: 7px; border: 1px solid #e5e9f0; background: #fff; color: #4b5563; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .12s, border-color .12s; }
  .dw-tool-btn:hover:not(:disabled) { background: #f3e8ff; border-color: #673AB7; color: #673AB7; }
  .dw-tool-btn:disabled { opacity: .35; cursor: not-allowed; }
  .dw-tool-remove:hover { background: #fee2e2 !important; border-color: #dc2626 !important; color: #dc2626 !important; }
  .dw-size-select { font-size: 11px; border: 1px solid #e5e9f0; border-radius: 7px; padding: 3px 6px; background: #fff; color: #4b5563; font-family: inherit; cursor: pointer; }

  .dw-card-loading { display: flex; flex-direction: column; gap: 8px; }

  .dw-stat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .dw-stat-cell { background: #f8fafc; border-radius: 12px; padding: 10px 12px; }
  .dw-stat-cell-val { display: block; font-size: 19px; font-weight: 800; color: #1a1f36; line-height: 1.15; }
  .dw-stat-cell-label { display: block; font-size: 10.5px; color: #9ca3af; font-weight: 600; margin-top: 3px; }

  .dw-bar-list { display: flex; flex-direction: column; gap: 10px; }
  .dw-bar-row { display: grid; grid-template-columns: 84px 1fr 34px; align-items: center; gap: 8px; }
  .dw-bar-name { font-size: 11.5px; color: #4b5563; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dw-bar-track { height: 7px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
  .dw-bar-fill { height: 100%; border-radius: 4px; transition: width .5s ease; }
  .dw-bar-val { font-size: 11.5px; font-weight: 700; color: #1a1f36; text-align: right; }

  .dw-row-list { display: flex; flex-direction: column; }
  .dw-scroll { max-height: 300px; overflow-y: auto; }
  .dw-rating-single { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 14px 0; }
  .dw-rating-num { font-size: 20px; font-weight: 800; color: #1a1f36; }
  .dw-rating-sub { font-size: 11px; color: #9ca3af; font-weight: 500; }

  .dw-time-table { display: flex; flex-direction: column; }
  .dw-time-row { display: grid; grid-template-columns: 1.6fr .7fr .7fr .7fr 1.3fr; align-items: center; gap: 6px; padding: 7px 0; border-bottom: 1px solid #f4f6fa; font-size: 11.5px; }
  .dw-time-row:last-child { border-bottom: none; }
  .dw-time-head { color: #9ca3af; font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; border-bottom: 1.5px solid #eef1f6; }
  .dw-time-name { display: flex; align-items: center; gap: 6px; font-weight: 600; color: #1a1f36; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dw-time-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .dw-time-total { display: flex; flex-direction: column; gap: 3px; font-weight: 700; color: #1a1f36; }
  .dw-time-total-track { height: 4px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
  .dw-time-total-fill { display: block; height: 100%; border-radius: 3px; }

  .dw-drawer { background: #fff; border-radius: 16px; width: 100%; max-width: 460px; max-height: 88vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.2); }
  .dw-drawer-body { padding: 12px 20px; overflow-y: auto; flex: 1; }
  .dw-drawer-group { margin-bottom: 16px; }
  .dw-drawer-group-title { display: flex; align-items: center; gap: 8px; font-size: 11.5px; font-weight: 700; color: #673AB7; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 8px; }
  .dw-drawer-item { display: flex; align-items: center; gap: 10px; padding: 9px 4px; font-size: 13px; color: #1a1f36; font-weight: 500; cursor: pointer; border-radius: 8px; }
  .dw-drawer-item:hover { background: #f8fafc; }
  .dw-drawer-item input { width: 16px; height: 16px; accent-color: #673AB7; cursor: pointer; }

  /* ═══════════════ PHONE LAYOUT (≤ 767px) — desktop/tablet rules above are untouched ═══════════════ */
  @media (max-width: 767px) {
    .crm-root { overflow-x: clip; padding-bottom: 24px; }

    /* Top bar: stacks, scrolls away with the page (the app navbar already sticks) */
    .crm-topbar { position: static; flex-direction: column; align-items: stretch; gap: 12px; padding: 14px 14px; }
    .crm-page-title { font-size: 18px; }
    .crm-page-sub { flex-wrap: wrap; gap: 6px 10px; }
    .crm-topbar > div:last-child { flex-wrap: wrap; gap: 8px !important; }
    .crm-topbar .crm-btn-ghost, .crm-topbar .crm-btn-primary { flex: 1 1 calc(50% - 8px); justify-content: center; min-height: 44px; font-size: 13px; }
    .crm-topbar [title="Toggle Fullscreen"] { display: none; }

    /* Stat cards: tidy 2-up grid */
    .crm-stats-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 12px 12px 0; }
    .crm-stat-card { min-width: 0; padding: 12px; gap: 10px; border-radius: 14px; }
    .crm-stat-icon { width: 36px; height: 36px; font-size: 18px; border-radius: 10px; }
    .crm-stat-val { font-size: 22px; }
    .crm-stat-label { font-size: 11.5px; }

    /* Spacing */
    .crm-full-width-section, .crm-main-grid, .crm-activity-section { padding-left: 12px; padding-right: 12px; }
    .crm-main-grid { gap: 14px; padding-top: 12px; }
    .dw-section { padding: 8px 12px 4px; }
    .crm-main-grid--no-main .crm-col--side { grid-template-columns: 1fr; }

    /* "Your Widgets" header: title on top, search + Add widgets full width below */
    .crm-section-header { flex-wrap: wrap; }
    .dw-section > .crm-section-header > div:first-child { width: 100%; }
    .dw-section > .crm-section-header .crm-section-sub { display: none; }
    .dw-section > .crm-section-header > div:last-child { width: 100%; }
    .dw-search-wrap { flex: 1; min-width: 0; }
    .dw-search-input { width: 100%; font-size: 16px; padding: 11px 12px 11px 32px; }
    .dw-section > .crm-section-header .crm-btn-ghost { min-height: 44px; flex-shrink: 0; }

    /* Widgets: single column, roomier touch rows. Hover-only reorder/resize tools can't be used on touch. */
    .dw-grid { grid-template-columns: 1fr; gap: 12px; }
    .dw-size-sm, .dw-size-md, .dw-size-lg { grid-column: span 1; }
    .dw-card { cursor: default; }
    .dw-card:hover, .crm-stat-card:hover { transform: none; }
    .dw-card-head { padding: 12px 14px; }
    .dw-card-body { padding: 12px 14px 14px; }
    .dw-card-tools { display: none !important; }
    .dw-scroll { max-height: 360px; -webkit-overflow-scrolling: touch; }
    .crm-scroll { max-height: 340px; -webkit-overflow-scrolling: touch; }
    .crm-list-row, .crm-team-row { padding: 12px 14px; min-height: 56px; }
    .crm-list-name { font-size: 14px; }
    .crm-list-meta { font-size: 12px; }
    .crm-pill { font-size: 11.5px; padding: 4px 9px; }
    .dw-time-row { font-size: 11px; gap: 4px; }
    .crm-chart-wrap { height: 200px; padding: 12px; }
    .crm-activity-header { flex-direction: column; align-items: stretch; }
    .crm-activity-controls { width: 100%; }
    .crm-activity-controls > * { flex: 1 1 calc(50% - 8px); }
    .crm-select { min-width: 0; width: 100%; font-size: 16px; padding: 10px 12px; }

    /* Tabs: easier to hit, still swipe-scrollable */
    .crm-tabs { padding: 0 8px; }
    .crm-tab { padding: 13px 12px; font-size: 13px; }
    .crm-unified-header { padding: 8px 8px 0; }
    .crm-unified-tab { padding: 13px 14px; }

    /* Month picker opens as a bottom card instead of overflowing the screen */
    .crm-month-dropdown { position: fixed; left: 12px; right: 12px; top: auto; bottom: 12px; width: auto; }
    .crm-month-btn { padding: 12px 4px; font-size: 13px; }

    /* Modals / customise drawer / history: bottom sheets, big buttons, no iOS zoom */
    .crm-modal-overlay { align-items: flex-end; padding: 0; }
    .crm-modal, .dw-drawer { max-width: 100%; max-height: 92vh; max-height: 92dvh; }
    .sg2-history-modal-container { max-width: 100%; }
    .crm-modal-body, .dw-drawer-body { padding: 14px 16px; -webkit-overflow-scrolling: touch; }
    .crm-modal-footer { position: sticky; bottom: 0; background: #fff; padding: 12px 16px; }
    .crm-modal-footer .crm-btn-primary, .crm-modal-footer .crm-btn-ghost { flex: 1; justify-content: center; min-height: 46px; font-size: 14px; }
    .crm-modal-close { width: 40px; height: 40px; font-size: 20px; }
    .crm-form-grid { grid-template-columns: 1fr; }
    .crm-form-select, .crm-modal input, .crm-modal select, .crm-modal textarea { font-size: 16px; padding: 11px 12px; }
    .dw-drawer-item { padding: 13px 6px; font-size: 14px; }
    .dw-drawer-item input { width: 20px; height: 20px; }
    .sg2-history-lead-strip { padding: 12px 16px; }
    .sg2-history-card { padding: 12px; }
  }
`;

export default Dashboard;



















